<template>
  <div
    id="search-results"
    class="search-page"
    :style="{
      '--items-width': `${220}px`
    }"
  >
    <DefinePriceSelector v-slot="{ close }">
      <div>
        <label for="price" class="block text-sm font-normal text-neutral pl-2">
          {{ $t('search.price') }}
        </label>
        <div class="grid grid-cols-2 gap-1.5 mb-1.5">
          <AppSelector
            id="price"
            v-model="searchQueryParams.priceFrom"
            class="variant-outline md:w-[200px] hide-label"
            dropdown-class="!max-h-[320px]"
            :label="$t('search.priceFrom')"
            :title="$t('search.noMin')"
            deselectable
            :options="prices"
            type="number"
            name="price-from"
            @update:model-value="close"
          />
          <AppSelector
            v-model="searchQueryParams.priceTo"
            class="variant-outline md:w-[200px] hide-label"
            dropdown-class="!max-h-[320px]"
            :label="$t('search.priceTo')"
            :title="$t('search.noMax')"
            deselectable
            :options="maxPrices"
            type="number"
            name="price-to"
            @update:model-value="close"
          />
        </div>
      </div>
    </DefinePriceSelector>
    <DefineBedroomsPanel v-slot="{ close }">
      <div>
        <label
          for="bedrooms"
          class="block text-sm font-normal text-neutral pl-2"
        >
          {{ $t('search.bedrooms') }}
        </label>
        <div class="grid grid-cols-2 gap-1.5 mb-1.5">
          <AppSelector
            id="bedrooms"
            v-model="searchQueryParams.bedroomsFrom"
            class="variant-outline md:w-[200px] hide-label"
            dropdown-class="!max-h-[320px]"
            :label="$t('search.bedroomsFrom')"
            :title="$t('search.noMin')"
            deselectable
            :options="bedroomsOptions"
            type="number"
            name="bedrooms-from"
            @update:model-value="close"
          />
          <AppSelector
            v-model="searchQueryParams.bedroomsTo"
            class="variant-outline md:w-[200px] hide-label"
            dropdown-class="!max-h-[320px]"
            :label="$t('search.bedroomsTo')"
            :title="$t('search.noMax')"
            deselectable
            :options="maxBedroomsOptions"
            type="number"
            name="bedrooms-to"
            @update:model-value="close"
          />
        </div>
      </div>
    </DefineBedroomsPanel>
    <DefineBathroomsPanel v-slot="{ close }">
      <label
        for="bathsFull"
        class="!block w-full block text-sm font-normal text-neutral pl-2"
      >
        {{ $t('search.bathrooms') }}
      </label>
      <div class="grid grid-cols-2 gap-1.5 mb-1.5">
        <AppSelector
          id="bathsFull"
          v-model="searchQueryParams.bathroomsFrom"
          class="variant-outline md:w-[200px] hide-label"
          dropdown-class="!max-h-[320px]"
          :label="$t('search.bathroomsFrom')"
          :title="$t('search.noMin')"
          deselectable
          :options="bathsOptions"
          type="number"
          name="baths-full-from"
          @update:model-value="close"
        />
        <AppSelector
          v-model="searchQueryParams.bathroomsTo"
          class="variant-outline md:w-[200px] hide-label"
          dropdown-class="!max-h-[320px]"
          :label="$t('search.bathroomsTo')"
          :title="$t('search.noMax')"
          deselectable
          :options="maxBathsOptions"
          type="number"
          name="baths-full-to"
          @update:model-value="close"
        />
      </div>
    </DefineBathroomsPanel>
    <DefineLocationFieldsPanel v-slot="{ close }">
      <div class="grid grid-cols-2 gap-1.5 mb-1.5 w-full">
        <AppSelector
          :model-value="searchQueryParams.county"
          name="county"
          :label="$t('search.county')"
          :placeholder="$t('search.county')"
          class="variant-outline col-span-2"
          :options="countyOptions"
          deselectable
          @item-selected="
            item => {
              setCounty(item);
              close && close();
            }
          "
        />
        <AppSelector
          :model-value="searchQueryParams.city"
          name="city"
          :label="$t('search.city')"
          :placeholder="$t('search.city')"
          class="variant-outline col-span-2 md:col-span-1 md:w-[200px]"
          :options="cityOptions"
          deselectable
          @item-selected="
            item => {
              setCity(item);
              close && close();
            }
          "
        />
        <AppSelector
          :model-value="searchQueryParams.zipcode"
          name="zipcode"
          :label="$t('search.zipcode')"
          :placeholder="$t('search.zipcode')"
          class="variant-outline col-span-2 md:col-span-1 md:w-[200px]"
          :options="zipcodeOptions"
          deselectable
          @item-selected="
            item => {
              setZipcode(item);
              close && close();
            }
          "
        />
        <AppSelectorWithSearch
          :model-value="searchQueryParams.neighborhood"
          name="neighborhood"
          :label="$t('search.neighborhood')"
          :placeholder="$t('search.neighborhood')"
          class="variant-outline col-span-2"
          :options="neighborhoodOptions"
          clearable
          deselectable
          @item-selected="
            item => {
              setNeighborhood(item);
              close && close();
            }
          "
          @update:search="e => (neighborhoodsSearch = e || '')"
        />
      </div>
    </DefineLocationFieldsPanel>
    <DefineOtherPanel v-slot="{ close }">
      <div class="flex flex-wrap gap-1.5 md:flex-col">
        <div class="min-w-[var(--items-width)] flex-1">
          <label for="year" class="block text-sm font-normal text-neutral pl-2">
            {{ $t('search.year') }}
          </label>
          <div class="grid grid-cols-2 gap-1.5">
            <AppSelector
              id="year"
              v-model="searchQueryParams.yearFrom"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.yearFrom')"
              :title="$t('search.noMin')"
              :options="
                yearOptions.map(value => ({ text: value.toString(), value }))
              "
              type="number"
              name="year-from"
              @update:model-value="close"
            />
            <AppSelector
              v-model="searchQueryParams.yearTo"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.yearTo')"
              :title="$t('search.noMax')"
              :options="
                maxYears.map(value => ({ text: value.toString(), value }))
              "
              type="number"
              name="year-to"
              @update:model-value="close"
            />
          </div>
        </div>
        <div class="min-w-[var(--items-width)] flex-1">
          <label for="area" class="block text-sm font-normal text-neutral pl-2">
            {{ $t('search.area') }}
          </label>
          <div class="grid grid-cols-2 gap-1.5 mb-1.5">
            <AppSelector
              id="area"
              v-model="searchQueryParams.areaFrom"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.areaFrom')"
              :title="$t('search.noMin')"
              deselectable
              :options="areaOptions"
              type="number"
              name="area-from"
              @update:model-value="close"
            />
            <AppSelector
              v-model="searchQueryParams.areaTo"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.areaTo')"
              :title="$t('search.noMax')"
              deselectable
              :options="maxAreas"
              type="number"
              name="area-to"
              @update:model-value="close"
            />
          </div>
        </div>
      </div>
    </DefineOtherPanel>
    <DefineAdvancedSearchPanel v-slot="{ close }">
      <div class="flex flex-col gap-1.5">
        <!--      <AppSwitch-->
        <!--        v-model="searchQueryParams.furnished"-->
        <!--        name="furnished"-->
        <!--        :label="$t('search.furnished')"-->
        <!--        class="block text-sm font-normal text-neutral pl-2"-->
        <!--      />-->
        <AppSelector
          v-model="searchQueryParams.view"
          name="view"
          :label="$t('search.view')"
          :placeholder="$t('search.view')"
          class="variant-outline w-full"
          :disabled="searchingBuildings"
          :options="filteredViewOptions"
          :loading-options="loadingViews"
          deselectable
          @open="updateViews"
          @update:model-value="close"
        />
        <div>
          <label for="cost" class="block text-sm font-normal text-neutral pl-2">
            {{ $t('search.condoCost') }}
          </label>
          <div class="grid grid-cols-2 gap-1.5">
            <AppSelector
              id="cost"
              v-model="searchQueryParams.condoCostFrom"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.condoCostFrom')"
              :title="$t('search.noMin')"
              deselectable
              :disabled="searchingBuildings"
              :options="condoCostOptions"
              type="number"
              name="cost-from"
              @update:model-value="close"
            />
            <AppSelector
              v-model="searchQueryParams.condoCostTo"
              class="variant-outline md:w-[200px] hide-label"
              dropdown-class="!max-h-[320px]"
              :label="$t('search.condoCostTo')"
              :title="$t('search.noMax')"
              deselectable
              :disabled="searchingBuildings"
              :options="maxCondoCost"
              type="number"
              name="cost-to"
              @update:model-value="close"
            />
          </div>
        </div>
        <AppSelector
          id="hopa"
          v-model="searchQueryParams.hopa"
          class="variant-outline w-full"
          dropdown-class="!max-h-[320px]"
          :label="$t('search.hopa')"
          deselectable
          :disabled="searchingBuildings"
          :options="hopaOptions"
          name="hopa"
          @update:model-value="close"
        />
        <AppSwitch
          v-if="
            searchQueryParams.buyType === 'RNT' ||
            searchQueryParams.buyType === 'RTD'
          "
          v-model="searchQueryParams.shortTermLease"
          name="shortTermLease"
          :label="$t('search.shortTermLease')"
          class="text-base font-normal text-neutral py-1"
        />
        <AppSwitch
          v-if="searchQueryParams.buyType === 'RES'"
          :model-value="!searchQueryParams.excludePendingAndUnderContract"
          name="pendingAndUnderContract"
          :label="$t('search.pendingAndUnderContract')"
          class="text-base font-normal text-neutral py-1"
          @update:model-value="
            value => {
              searchQueryParams.excludePendingAndUnderContract = !value;
              // close();
            }
          "
        />
        <AppSwitch
          v-model="searchQueryParams.inListing"
          name="inListing"
          :label="$t('search.inListing')"
          class="text-base font-normal text-neutral py-1"
        />
        <!--      <AppSwitch-->
        <!--        v-model="searchQueryParams.dock"-->
        <!--        name="dock"-->
        <!--        :label="$t('search.dock')"-->
        <!--        class="text-base font-normal text-neutral"-->
        <!--      />-->
      </div>
    </DefineAdvancedSearchPanel>
    <SiteNavigationBar />
    <Transition name="modal">
      <AppModal
        v-if="authModalPromise"
        class="modal-container fix-page"
        window-class="max-w-[calc(100vw-16px)] max-h-[calc(100svh-32px)] min-[412px]:max-w-[480px] w-full bg-white rounded-md md:rounded-lg shadow-lg py-2 md:p-4 overflow-y-auto"
        @click="authModalPromise?.resolve()"
      >
        <SiteAuth class="w-full" :title="$t('auth.loginToSavePropertyHint')" />
      </AppModal>
    </Transition>
    <main class="px-4">
      <div
        id="search-section"
        class="!min-h-[100lvh] min-h-[100vh] pt-[88px] max-md:pt-[60px] flex flex-col items-start justify-start"
      >
        <div class="sr-only">
          {{ $t('search.title') }}
        </div>
        <div
          class="top-section flex justify-start flex-wrap gap-1.5 py-2 sticky top-[60px] md:top-[88px] z-[54] w-[calc(100%+2rem)] pl-4 -ml-4 bg-white border-b border-line-light"
        >
          <div
            id="search-controls"
            class="w-full max-w-[calc(100vw-2rem)] flex flex-col gap-2 bg-white"
          >
            <div class="w-full max-md:hidden flex flex-wrap gap-2">
              <SiteSearchBar
                v-model="searchQueryLocation"
                class="max-md:hidden w-full max-w-[580px] variant-outline search-bar flex-1"
                field-name="mobile-search-bar"
                :placeholder="$t('search.searchPlaceholder')"
                :search-new="!propertiesFilters"
                use-location-from-store
                @update:search="searchQueryString = $event"
              >
                <template #button>
                  <Icon
                    class="text-neutral-light w-6 h-full"
                    :path="mdiMapMarkerOutline"
                  />
                </template>
              </SiteSearchBar>
              <AppSelector
                v-if="propertiesFilters"
                :model-value="searchQueryParams.buyType"
                class="variant-search active-filter-on-selected"
                :options="buyTypeOptions"
                deselectable
                name="buy-type-properties"
                @update:model-value="setBuyType"
              />
              <AppSelector
                v-if="!propertiesFilters"
                v-model="projectBuyType"
                class="variant-search active-filter-on-selected"
                :options="buyTypeOptions"
                name="buy-type-project"
              />
              <AppSelector
                v-if="!propertiesFilters && false"
                v-model="searchQueryParams.projectType"
                class="variant-search"
                :options="projectTypeOptions"
                deselectable
                name="project-type"
              />
              <AppSelector
                id="propertyType"
                ref="propertyTypeSelector"
                :model-value="searchQueryParams.propertyType"
                multiple
                :title="propertyTypeButtonText"
                class="variant-search active-filter-on-selected"
                :show-value="false"
                deselectable
                :options="propertyTypesOptions"
                name="property-type"
                @update:model-value="setPropertyType"
              >
                <SearchResetButton
                  v-if="searchQueryParams.propertyType?.length"
                  class="!absolute top-[calc(50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="searchQueryParams.propertyType = []"
                />
              </AppSelector>
              <AppSelector
                v-if="searchQueryParams.propertyType?.length"
                id="propertyType"
                ref="propertyTypeSelector"
                :model-value="searchQueryParams.propertySubType"
                multiple
                :title="propertySubTypeButtonText"
                class="variant-search active-filter-on-selected"
                :show-value="false"
                deselectable
                :options="filteredSubTypesOptions"
                name="property-sub-type"
                @update:model-value="setSubType"
              >
                <SearchResetButton
                  v-if="searchQueryParams.propertySubType?.length"
                  class="!absolute top-[calc(50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="searchQueryParams.propertySubType = []"
                />
              </AppSelector>
              <div class="relative h-[36px] md:h-[42px]">
                <AppPopover
                  v-if="propertiesFilters"
                  class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                  container-class="inline-block"
                  name="price"
                >
                  <AppButton
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter':
                        searchQueryParams.priceFrom || searchQueryParams.priceTo
                    }"
                  >
                    <span class="mr-auto">{{ priceButtonText }}</span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <AppButton
                      class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-4"
                      @click="() => closeDropdown('price')"
                    >
                      {{ $t('common.done') }}
                    </AppButton>
                    <PriceSelector :close="() => {}" />
                  </template>
                </AppPopover>
                <AppPopover
                  v-if="!propertiesFilters"
                  class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                  container-class="inline-block"
                  name="price"
                >
                  <AppButton
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter':
                        searchQueryParams.priceFrom || searchQueryParams.priceTo
                    }"
                  >
                    <span class="mr-auto">{{ priceButtonText }}</span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <AppButton
                      class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-4"
                      @click="() => closeDropdown('price')"
                    >
                      {{ $t('common.done') }}
                    </AppButton>
                    <PriceSelector :close="() => {}" />
                  </template>
                </AppPopover>
                <SearchResetButton
                  v-if="
                    searchQueryParams.priceFrom || searchQueryParams.priceTo
                  "
                  class="absolute top-[calc(-50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="resetFilters(['priceFrom', 'priceTo'])"
                />
              </div>
              <div
                v-if="propertiesFilters"
                class="relative h-[36px] md:h-[42px]"
              >
                <AppPopover
                  class="left-0 w-max p-4 flex flex-col items-start"
                  container-class="inline-block"
                  name="beds-baths"
                >
                  <AppButton
                    :title="bedroomsAndBathroomsButtonText"
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter':
                        searchQueryParams.bedroomsFrom ||
                        searchQueryParams.bedroomsTo ||
                        searchQueryParams.bathroomsFrom ||
                        searchQueryParams.bathroomsTo
                    }"
                  >
                    <span class="mr-auto">
                      {{ bedroomsAndBathroomsButtonText }}
                    </span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <AppButton
                      class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-4"
                      @click="() => closeDropdown('beds-baths')"
                    >
                      {{ $t('common.done') }}
                    </AppButton>
                    <BedroomsPanel :close="() => {}" />
                    <BathroomsPanel :close="() => {}" />
                  </template>
                </AppPopover>
                <SearchResetButton
                  v-if="
                    searchQueryParams.bedroomsFrom ||
                    searchQueryParams.bedroomsTo ||
                    searchQueryParams.bathroomsFrom ||
                    searchQueryParams.bathroomsTo
                  "
                  class="absolute top-[calc(-50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="
                    resetFilters([
                      'bedroomsFrom',
                      'bedroomsTo',
                      'bathroomsFrom',
                      'bathroomsTo'
                    ])
                  "
                />
              </div>
              <div
                v-if="!propertiesFilters"
                class="relative h-[36px] md:h-[42px]"
              >
                <AppPopover
                  class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                  container-class="inline-block"
                  name="location"
                >
                  <AppButton
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter':
                        searchQueryParams.county ||
                        searchQueryParams.city ||
                        searchQueryParams.zipcode ||
                        searchQueryParams.neighborhood ||
                        searchQueryParams.bedroomsFrom ||
                        searchQueryParams.bedroomsTo ||
                        searchQueryParams.bathroomsFrom ||
                        searchQueryParams.bathroomsTo ||
                        searchQueryParams.areaFrom ||
                        searchQueryParams.areaTo
                    }"
                  >
                    <span class="mr-auto">{{ $t('search.moreOptions') }}</span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <div
                      class="max-h-[calc(100vh-275px)] overflow-auto -mt-3 pt-3"
                    >
                      <AppButton
                        class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-4"
                        @click="() => closeDropdown('location')"
                      >
                        {{ $t('common.done') }}
                      </AppButton>
                      <LocationFieldsPanel :close="() => {}" />
                      <OtherPanel :close="() => {}" />
                      <BedroomsPanel :close="() => {}" />
                      <BathroomsPanel :close="() => {}" />
                    </div>
                  </template>
                </AppPopover>
                <SearchResetButton
                  v-if="
                    searchQueryParams.county ||
                    searchQueryParams.city ||
                    searchQueryParams.zipcode ||
                    searchQueryParams.neighborhood ||
                    searchQueryParams.bedroomsFrom ||
                    searchQueryParams.bedroomsTo ||
                    searchQueryParams.bathroomsFrom ||
                    searchQueryParams.bathroomsTo ||
                    searchQueryParams.areaFrom ||
                    searchQueryParams.areaTo
                  "
                  class="absolute top-[calc(-50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="
                    resetFilters([
                      'county',
                      'city',
                      'zipcode',
                      'neighborhood',
                      'bedroomsFrom',
                      'bedroomsTo',
                      'bathroomsFrom',
                      'bathroomsTo',
                      'areaFrom',
                      'areaTo'
                    ])
                  "
                />
              </div>
              <div
                v-if="propertiesFilters"
                class="relative h-[36px] md:h-[42px]"
              >
                <AppPopover
                  class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                  container-class="inline-block"
                  name="other"
                >
                  <AppButton
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter':
                        searchQueryParams.county ||
                        searchQueryParams.city ||
                        searchQueryParams.zipcode ||
                        searchQueryParams.neighborhood ||
                        searchQueryParams.areaFrom ||
                        searchQueryParams.areaTo ||
                        searchQueryParams.yearFrom ||
                        searchQueryParams.yearTo ||
                        searchQueryParams.furnished ||
                        searchQueryParams.view ||
                        searchQueryParams.condoCostFrom ||
                        searchQueryParams.condoCostTo ||
                        searchQueryParams.hopa ||
                        searchQueryParams.dock
                    }"
                  >
                    <span class="mr-auto">{{ $t('search.moreOptions') }}</span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <div
                      class="max-h-[calc(100vh-275px)] overflow-auto -mt-3 pt-3"
                    >
                      <AppButton
                        class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-4"
                        @click="() => closeDropdown('other')"
                      >
                        {{ $t('common.done') }}
                      </AppButton>
                      <LocationFieldsPanel :close="() => {}" />
                      <OtherPanel :close="() => {}" />
                      <AdvancedSearchPanel :close="() => {}" />
                    </div>
                  </template>
                </AppPopover>
                <SearchResetButton
                  v-if="
                    searchQueryParams.county ||
                    searchQueryParams.city ||
                    searchQueryParams.zipcode ||
                    searchQueryParams.neighborhood ||
                    searchQueryParams.areaFrom ||
                    searchQueryParams.areaTo ||
                    searchQueryParams.yearFrom ||
                    searchQueryParams.yearTo ||
                    searchQueryParams.furnished ||
                    searchQueryParams.view ||
                    searchQueryParams.condoCostFrom ||
                    searchQueryParams.condoCostTo ||
                    searchQueryParams.hopa ||
                    searchQueryParams.dock
                  "
                  class="absolute top-[calc(-50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="
                    resetFilters([
                      'county',
                      'city',
                      'zipcode',
                      'neighborhood',
                      'areaFrom',
                      'areaTo',
                      'yearFrom',
                      'yearTo',
                      'furnished',
                      'view',
                      'condoCostFrom',
                      'condoCostTo',
                      'hopa',
                      'dock'
                    ])
                  "
                />
              </div>
              <div
                v-if="!propertiesFilters && !isInMobile"
                class="relative h-[36px] md:h-[42px]"
              >
                <AppPopover
                  class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                  container-class="inline-block"
                  name="location"
                >
                  <AppButton
                    class="variant-search search-controls-button"
                    :class="{
                      'active-filter': Object.values(placesLayers).some(Boolean)
                    }"
                  >
                    <span class="mr-auto">{{ t('search.showPlaces') }}</span>
                    <Icon :path="mdiChevronDown" />
                  </AppButton>
                  <template #panel>
                    <div class="flex flex-col gap-1 max-h-[calc(100vh-275px)]">
                      <AppButton
                        class="variant-invisible px-2 py-1 ml-auto z-[1] rounded text-sm text-primary font-normal underline -mt-3 -mb-1"
                        @click="() => closeDropdown('location')"
                      >
                        {{ $t('common.done') }}
                      </AppButton>
                      <AppSwitch
                        v-for="layer in placesOptions"
                        :key="`layer-toggle-${layer.key}`"
                        v-model="placesLayers[layer.key]"
                        class="!text-neutral-light"
                        :name="`layer-toggle-${layer.key}`"
                        :title="$t(layer.title)"
                        :label="$t(layer.title)"
                      />
                    </div>
                  </template>
                </AppPopover>
                <SearchResetButton
                  v-if="Object.values(placesLayers).some(Boolean)"
                  class="absolute top-[calc(-50%-0.5rem)] left-[calc(100%-1.5rem)]"
                  @click="placesLayers = { ...DEFAULT_PLACES_LAYERS }"
                />
              </div>
              <AppButton
                class="px-2 h-[36px] md:h-[42px] text-sm rounded shadow"
                @click="saveSearch"
              >
                <template v-if="searchSaved">
                  <span class="text-[0.8em]">
                    {{ $t('search.searchSaved') }}
                  </span>
                  <Icon
                    class="w-4 h-4 min-w-4 mr-[-5px]"
                    :path="mdiCheckBold"
                  />
                </template>
                <template v-else>
                  {{ $t('search.saveSearch') }}
                </template>
              </AppButton>
            </div>
            <div
              class="w-full md:hidden flex flex-wrap gap-2 md:gap-4 max-md:sticky top-[60px] bg-white z-[22] min-w-[calc(100%+1rem)] md:min-w-[calc(100%+2rem)] -mx-4 px-4"
            >
              <SiteSearchBar
                v-model="searchQueryLocation"
                class="variant-outline w-full h-[48px] fix-h"
                field-name="search-bar"
                :placeholder="$t('search.searchPlaceholder')"
                :search-new="!propertiesFilters"
                use-location-from-store
                @update:search="searchQueryString = $event"
              >
                <template #button>
                  <Icon
                    class="text-neutral-light w-8 h-full"
                    :path="mdiMapMarkerOutline"
                  />
                </template>
              </SiteSearchBar>
              <AppSelector
                v-if="propertiesFilters"
                :model-value="searchQueryParams.buyType"
                class="variant-search"
                :options="buyTypeOptions"
                deselectable
                name="buy-type"
                @update:model-value="setBuyType"
              />
              <AppSelector
                v-if="!propertiesFilters"
                v-model="projectBuyType"
                class="variant-search active-filter-on-selected"
                :options="buyTypeOptions"
                name="buy-type-project-m"
              />
              <AppButton
                id="filters-button"
                class="variant-search search-controls-button"
                :class="{
                  'popover-active': filtersMenu,
                  'active-filter':
                    searchQueryParams.county ||
                    searchQueryParams.city ||
                    searchQueryParams.zipcode ||
                    searchQueryParams.neighborhood ||
                    searchQueryParams.priceFrom ||
                    searchQueryParams.priceTo ||
                    searchQueryParams.bedroomsFrom ||
                    searchQueryParams.bedroomsTo ||
                    searchQueryParams.bathroomsFrom ||
                    searchQueryParams.bathroomsTo ||
                    searchQueryParams.areaFrom ||
                    searchQueryParams.areaTo ||
                    searchQueryParams.yearFrom ||
                    searchQueryParams.yearTo ||
                    searchQueryParams.furnished ||
                    searchQueryParams.view ||
                    searchQueryParams.condoCostFrom ||
                    searchQueryParams.condoCostTo ||
                    searchQueryParams.propertyType?.length ||
                    searchQueryParams.hopa ||
                    searchQueryParams.dock
                }"
                @click="filtersMenu = !filtersMenu"
              >
                <span class="mr-auto">
                  {{ $t('search.filters') }}
                </span>
                <Icon :path="mdiChevronDown" />
              </AppButton>
              <AppButton
                class="px-2 md:px-4 h-[32px] md:h-[36px] text-sm rounded shadow"
                @click="saveSearch"
              >
                <template v-if="searchSaved">
                  <span class="text-[0.8em]">
                    {{ $t('search.searchSaved') }}
                  </span>
                  <Icon
                    class="w-4 h-4 min-w-4 mr-[-5px]"
                    :path="mdiCheckBold"
                  />
                </template>
                <template v-else>
                  {{ $t('search.saveSearch') }}
                </template>
              </AppButton>
            </div>
          </div>
        </div>
        <Transition
          enter-active-class="transition duration-[0.31] ease-in-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-[0.21] ease-in-out"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="filtersMenu"
            class="bg-white fixed left-0 top-0 z-[61] h-[100lvh] w-full overflow-y-auto"
          >
            <div
              class="flex relative h-[60px] w-full justify-between items-center border-b border-line-light"
            >
              <h2
                class="text-base min-[400px]:text-lg w-full text-center font-semibold"
              >
                {{ resultTitle || $t('search.filters') }}
              </h2>
              <AppButton
                class="icon-button variant-invisible !absolute right-0 top-0 !p-0 h-full aspect-square text-neutral"
                :icon="mdiClose"
                :aria-label="$t('search.closeFilters')"
                @click="filtersMenu = false"
              />
            </div>
            <div
              id="filters-container"
              class="flex flex-col items-start p-4 overflow-y-auto max-h-[calc(100svh-132px)]"
            >
              <div class="w-full h-full overflow-auto">
                <label
                  for="propertyType"
                  class="block text-sm font-normal text-neutral pl-2"
                >
                  {{ $t('search.propertyType') }}
                </label>
                <AppSelector
                  id="propertyType"
                  ref="propertyTypeSelector"
                  :model-value="searchQueryParams.propertyType"
                  multiple
                  :title="propertyTypeButtonText"
                  class="variant-outline active-filter-on-selected w-full mb-2"
                  :show-value="false"
                  deselectable
                  :options="propertyTypesOptions"
                  @update:model-value="setPropertyType"
                />
                <AppSelector
                  v-if="searchQueryParams.propertyType?.length"
                  id="propertySubType"
                  ref="propertySubTypeSelector"
                  v-model="searchQueryParams.propertySubType"
                  :title="propertySubTypeButtonText"
                  class="variant-outline active-filter-on-selected w-full mb-2"
                  :show-value="false"
                  deselectable
                  :options="filteredSubTypesOptions"
                />
                <PriceSelector />
                <BedroomsPanel />
                <BathroomsPanel />
                <LocationFieldsPanel />
                <OtherPanel />
                <AdvancedSearchPanel v-if="propertiesFilters" />
              </div>
            </div>
            <div class="flex w-full p-4 justify-end gap-4 mt-auto">
              <AppButton
                class="max-lg:text-sm text-base px-4 max-lg:h-[40px] h-[48px] rounded"
                @click="resetFilters"
              >
                {{ $t('search.resetFilters') }}
              </AppButton>
              <AppButton
                class="max-lg:text-sm text-base px-4 max-lg:h-[40px] h-[48px] rounded"
                @click="filtersMenu = false"
              >
                {{ $t('search.acceptFilters') }}
              </AppButton>
            </div>
          </div>
        </Transition>
        <div id="results" class="">
          <div
            class="side-section"
            :class="{
              'md:!col-[1/8] min-h-[calc(100vh-420px)]':
                baseResult && cardResults.length === 0
            }"
          >
            <ClientOnly>
              <SiteSearchMap3D
                v-if="use3DMap"
                id="map-container"
                ref="mapProjects"
                v-model:clicked-pin="clickedPin"
                v-model:hovered-item="hoveredItem"
                class="relative"
                :class="{
                  'show-map': showMap
                }"
                :markers="(shownMarkers as ProjectCardPropsExtended[])"
                :get-card-data="(id: string) => cardsData[id]"
                :places-layers="placesLayers"
                :show-map="showMap"
                :favorites="favorites"
                interactive-markers
                store-position
                @update:favorite="
                  (item, e) => setPropertyAsFavorite(item as CardProps, e)
                "
              />
              <SiteSearchMap
                v-else
                ref="mapDefault"
                :bounds="bounds"
                v-model:clicked-pin="clickedPin"
                v-model:hovered-item="hoveredItem"
                v-model:using-polygon="usingPolygon"
                class="relative"
                :class="{
                  'show-map': showMap
                }"
                :markers="shownMarkers"
                :get-card-data="(id: string) => cardsData[id]"
                :custom-polygon="multiPolygon"
                :follow-hovered-item="omitBounds"
                :location-pin="
                  searchQueryLocation?.type === 'geolocation' &&
                  searchQueryLocation?.value !== 'current-location' &&
                  searchQueryLocation?.value?.split(',')?.[1]
                    ? {
                        lat: +searchQueryLocation?.value?.split(',')?.[0] || 0,
                        lng: +searchQueryLocation?.value?.split(',')?.[1] || 0
                      }
                    : null
                "
                :show-map="showMap"
                :favorites="favorites"
                polygon-controls
                @update:favorite="
                  (item, e) => setPropertyAsFavorite(item as CardProps, e)
                  "
                @update:bounds="
                  bounds = {
                    ...$event,
                    county: searchQueryParams.county,
                    city: searchQueryParams.city,
                    zipcode: searchQueryParams.zipcode
                  }
                "
                @push-custom-polygon="pushCustomPolygon"
                @bounds-updated="
                  () => {
                    if (page !== 1 && firstBoundUpdateDone) {
                      page = 1;
                    }
                    firstBoundUpdateDone = true;
                    triggerSearch();
                  }
                "
                @clear-polygon="clearPolygon"
              />
            </ClientOnly>
          </div>
          <div
            class="results-section"
            :class="{
              'disable-cards': !cardsUpdated
            }"
          >
            <div v-if="resultTitle || searchSummary" class="pt-1 pl-1 w-full">
              <h1
                v-if="searchSummary"
                class="text-neutral text-lg font-bold leading-7 [text-wrap:balance]"
              >
                {{ searchSummary }}
              </h1>
              <div class="text-neutral-light leading-5">
                {{ resultTitle || '&nbsp;' }}
              </div>
            </div>
            <div
              v-if="cardResults.length == 0 && baseResult"
              class="flex flex-col items-center justify-center py-20 w-full h-full md:max-h-[calc(100svh-147px)] text-center"
            >
              <h2 class="text-lg font-bold">
                {{ t('common.noItemsFound') }}
              </h2>
              <div class="text-base mb-4">
                {{ t('common.tryAdjustingFilters') }}
              </div>
              <button type="button" class="text-primary" @click="resetFilters">
                {{ $t('search.resetFiltersLong') }}
              </button>
            </div>
            <template v-if="categorizedCards.length && baseResult">
              <template
                v-for="(category, index) in categorizedCards"
                :key="`${index}-${category.cards?.[0]?.id}-cards`"
              >
                <h2
                  v-if="category.title"
                  class="category-title w-full text-md uppercase font-bold mb-1 mt-2 text-neutral pl-1"
                >
                  {{ category.title }}
                </h2>
                <component
                  :is="
                    !item.to
                      ? SitePropertyCardPlaceholder
                      : item.projectStatus
                      ? SiteProjectCard
                      : SitePropertyCard
                  "
                  v-for="item in category.cards"
                  :key="`${item.images?.length}-${item.id}-card`"
                  :favorite="favorites[item.id]"
                  class="result-card max-sm:w-full max-sm:aspect-auto"
                  v-bind="item"
                  show-favorite
                  :h="category.title ? 3 : 2"
                  @mouseover="
                    () => {
                      hoveredItem = item.id;
                      clickedPin = null;
                    }
                  "
                  @mouseleave="item.id === hoveredItem && (hoveredItem = null)"
                  @update:favorite="setPropertyAsFavorite(item, $event)"
                ></component>
              </template>
            </template>
            <template v-else-if="!baseResult">
              <SitePropertyCardPlaceholder
                v-for="(_, index) in Array.from({ length: PER_PAGE })"
                :key="`${index}-placeholder`"
                :data-name="`${index}-placeholder`"
              >
              </SitePropertyCardPlaceholder>
              <div class="hidden">
                <SiteProjectCard
                  v-bind="{
                    id: 'preload',
                    projectStatus: 'pre-construction',
                    imgAlt: 'preload',
                    images: [],
                    name: 'preload',
                    location: 'preload',
                    yearCompletion: 0,
                    minPrice: 0,
                    resultIndex: 0,
                    to: 'preload',
                    type: 'condo'
                  }"
                />
                <SitePropertyCard
                  v-bind="{
                    id: 'preload',
                    mlsId: 0,
                    imgAlt: 'preload',
                    images: [],
                    price: 0,
                    info: 'preload',
                    address: 'preload',
                    postalCode: 'preload',
                    resultIndex: 0,
                    to: 'preload'
                  }"
                />
              </div>
            </template>
            <div
              v-if="totalPages > 1"
              id="pagination"
              class="w-full text-center mb-2"
            >
              <AppPagination v-model="page" :total="totalPages" />
            </div>
            <SiteBreadcrumbs
              :items="breadcrumbs"
              class="w-full !p-0 min-h-[32px] md:max-h-[48px] items-center -my-1"
            />
            <SiteFooter
              v-once
              class="-mx-4 narrow !bg-white !text-neutral -mt-4"
              :fix-locale="locale"
              logo-path="/images/pfs-logo-black.svg"
            />
          </div>
        </div>
        <div
          class="h-[48px] bottom-[20px] sm:bottom-[24px] z-[58] !fixed absolute-center-x bg-blue-dark rounded"
        >
          <Transition
            mode="out-in"
            enter-active-class="transition duration-[0.31] ease-in-out"
            enter-from-class="transform scale-100 opacity-100"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-[0.31] ease-in-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-100 opacity-0"
          >
            <div
              v-if="!showMap"
              class="flex items-center justify-center whitespace-nowrap h-[48px] w-full"
            >
              <AppButton
                class="md:hidden text-white variant-invisible h-full rounded-l px-3 py-2"
                @click="showMap = !showMap"
              >
                {{ $t('search.map') }}
              </AppButton>
              <div class="md:hidden h-[24px] w-[2px] bg-white -mx-px"></div>
              <VDropdown popper-class="reset-dropdown" strategy="fixed">
                <AppButton
                  class="text-white variant-invisible h-[48px] rounded-r px-3 py-2"
                >
                  {{ $t('search.sortBy') }}
                </AppButton>
                <template id="sort-by-dropdown" #popper="{ hide }">
                  <div
                    v-if="propertiesFilters"
                    id="sort-by-dropdown-content"
                    class="dropdown-menu py-1"
                  >
                    <div
                      v-for="option in sortByOptions"
                      :key="option.value"
                      class="menu-item !flex items-center"
                      @click="
                        () => {
                          searchQueryParams.sortBy = option.value;
                          if (page !== 1) page = 1;
                          hide();
                        }
                      "
                    >
                      <Icon
                        :path="
                          option.value === searchQueryParams.sortBy
                            ? mdiCheckBold
                            : ''
                        "
                        class="w-6 h-6 -ml-2 mr-1 text-primary"
                      />
                      <div>
                        {{ option.text }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="dropdown-menu py-1">
                    <div
                      v-for="option in sortProjectsByOptions"
                      :key="option.value"
                      class="menu-item !flex items-center"
                      @click="
                        () => {
                          searchQueryParams.sortBy = option.value;
                          if (page !== 1) page = 1;
                          close();
                        }
                      "
                    >
                      <Icon
                        :path="
                          option.value === searchQueryParams.sortBy
                            ? mdiCheckBold
                            : ''
                        "
                        class="w-6 h-6 -ml-2 mr-1 text-primary"
                      />
                      <div>
                        {{ option.text }}
                      </div>
                    </div>
                  </div>
                </template>
              </VDropdown>
            </div>
            <div
              v-else
              class="flex items-center justify-center whitespace-nowrap h-[48px] w-full"
            >
              <AppButton
                class="text-white variant-invisible h-full w-full rounded px-4 py-2"
                @click="showMap = !showMap"
              >
                {{ $t('search.listView') }}
                <Icon :path="mdiCardMultiple" />
              </AppButton>
            </div>
          </Transition>
        </div>
      </div>
    </main>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="CardProps extends PropertyCardProps | ProjectCardProps = PropertyCardProps & ProjectCardProps"
>
import {
  mdiCardMultiple,
  mdiCheckBold,
  mdiChevronDown,
  mdiClose,
  mdiMapMarkerOutline
} from '@mdi/js';
import { createReusableTemplate } from '@vueuse/core';
import { Dropdown as VDropdown } from 'floating-vue';
import { FocusTrap } from 'focus-trap-vue';
import type { PropType } from 'vue';

import 'floating-vue/dist/style.css';

import type {
  CityOption,
  CountyOption,
  ZipcodeOption
} from '@/helpers/locations';
import {
  formatShortPrice,
  getBedroomsAndBathroomsButtonText,
  getPriceButtonText,
  placesOptions,
  valuesMap
} from '@/helpers/search';
import { joinAnd } from '@/helpers/utils';
import { useSearchStore } from '@/store/search';
import { ExternalPromise } from '@/types/common';
import type { QueryValue } from '@/types/common';
import type {
  GroupQueryResponse,
  Layer,
  ProjectCardProps,
  ProjectCardPropsExtended,
  ProjectDocumentResponse,
  PropertyCardProps,
  PropertyCardPropsExtended,
  PropertyDocumentResponse,
  QueryResponse
} from '@/types/properties';
import type { PropertySubType, SearchParams } from '@/types/search';
import { debounce } from '@/utils/time';

const AppModal = defineAsyncComponent(
  () => import('@/async-components/App/Modal.vue')
);
const AppPagination = defineAsyncComponent(
  () => import('@/async-components/App/Pagination.vue')
);
const AppPopover = defineAsyncComponent(
  () => import('@/async-components/App/Popover.vue')
);
const AppSelector = defineAsyncComponent(
  () => import('@/async-components/App/Selector.vue')
);
const AppSelectorWithSearch = defineAsyncComponent(
  () => import('@/async-components/App/SelectorWithSearch.vue')
);
const AppSwitch = defineAsyncComponent(
  () => import('@/async-components/App/Switch.vue')
);
const SiteAuth = defineAsyncComponent(
  () => import('@/async-components/Site/Auth.vue')
);
const SiteBreadcrumbs = defineAsyncComponent(
  () => import('@/async-components/Site/Breadcrumbs.vue')
);
const SiteNavigationBar = defineAsyncComponent(
  () => import('@/async-components/Site/NavigationBar.vue')
);
const SitePropertyCardPlaceholder = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCardPlaceholder.vue')
);
const SiteProjectCard = defineAsyncComponent({
  loader: () => import('@/async-components/Site/ProjectCard.vue'),
  loadingComponent: SitePropertyCardPlaceholder,
  delay: 1
});
const SitePropertyCard = defineAsyncComponent({
  loader: () => import('@/async-components/Site/PropertyCard.vue'),
  loadingComponent: SitePropertyCardPlaceholder,
  delay: 1
});
const SiteSearchBar = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Bar.vue')
);
const SiteSearchMap = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Map.vue')
);
const SiteSearchMap3D = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Map3D.vue')
);
const SearchResetButton = defineAsyncComponent(
  () => import('@/async-components/Site/Search/ResetButton.vue')
);

type DocumentResponse = CardProps extends ProjectCardProps
  ? ProjectDocumentResponse
  : CardProps extends PropertyCardProps
  ? PropertyDocumentResponse
  : never;

type Dropdown = {
  close: () => void;
};

const [DefinePriceSelector, PriceSelector] = createReusableTemplate<Dropdown>();
const [DefineBedroomsPanel, BedroomsPanel] = createReusableTemplate<Dropdown>();
const [DefineBathroomsPanel, BathroomsPanel] =
  createReusableTemplate<Dropdown>();
const [DefineLocationFieldsPanel, LocationFieldsPanel] =
  createReusableTemplate<Dropdown>();
const [DefineOtherPanel, OtherPanel] = createReusableTemplate<Dropdown>();
const [DefineAdvancedSearchPanel, AdvancedSearchPanel] =
  createReusableTemplate<Dropdown>();

const {
  public: { apiUrl }
} = useRuntimeConfig();

interface SearchLocation {
  type:
    | 'location'
    | 'geolocation'
    | 'county'
    | 'city'
    | 'zip_code'
    | 'project'
    | 'building'
    | 'neighborhood'
    | 'address'
    | 'mls_id'; // from insta search
  value: string;
  text?: string;
  subType?: string;
  transaction_type?: string;
  itemId?: string;
  county?: string;
  city?: string;
  zipcode?: string;
  full?: string;
  address?: string;
  status?: string;
}

const searchStore = useSearchStore();

const { selectedLocation: searchQueryLocation } = useSelectableLocation(() => ({
  navigateToLocations: true,
  beforeNavigate: searchStore.saveQueryToStorage
}));

const DEFAULT_SEARCH_QUERY_PARAMS: SearchParams = {};

const isInMobile = ref(true);

const searchQueryParams = ref(DEFAULT_SEARCH_QUERY_PARAMS as SearchParams);

const neighborhoodsSearch = ref('');

const loadedNeighborhoodOptions = ref(
  [] as {
    value: string;
    text: string;
    county: string;
    city: string;
    zipcode: string;
  }[]
);

const DEFAULT_PLACES_LAYERS: Record<Layer, boolean> = {
  beaches: false,
  malls: false,
  restaurants: false,
  'college-universities': false,
  'public-schools': false,
  'private-schools': false,
  hospitals: false
};

const placesLayers = ref<Record<Layer, boolean>>({ ...DEFAULT_PLACES_LAYERS });

type PolygonResponse =
  | {
      type: 'Polygon';
      coordinates: [number, number][][];
    }
  | {
      type: 'MultiPolygon';
      coordinates: [number, number][][][];
    };

const polygonResponse = ref<PolygonResponse | null>(null);

type LatLngPolygon = { lat: number; lng: number }[][];

const customPolygon = ref<LatLngPolygon[] | null>(null);

const multiPolygon = computed<LatLngPolygon[] | null>(() => {
  if (customPolygon.value?.length) {
    return customPolygon.value;
  } else if (polygonResponse.value?.type === 'Polygon') {
    return [
      polygonResponse.value.coordinates[0].map(([lng, lat]) => ({ lat, lng }))
    ];
  } else if (polygonResponse.value?.type === 'MultiPolygon') {
    return polygonResponse.value.coordinates[0].map(p =>
      p.map(([lng, lat]) => ({ lat, lng }))
    );
  }

  return null;
});

const usingPolygon = ref(false);

const hideMap = computed(() => {
  if (customPolygon.value || usingPolygon.value) {
    return false;
  }

  return !cardResults.value.length || !baseResult.value;
});

const use3DMap = computed(() => {
  return props.collection === 'projects' && !isInMobile.value;
});

const { get } = useHttpMethods();

const user = inject('user') as Ref<{ id?: string; email?: unknown } | null>;
const isAuthenticated = computed(() => Boolean(user?.value?.email));

const localePath = useLocalePath();

const { t, locale } = useI18n();

const { language } = useLanguage();

const { triggerEvent } = useHubspot();

function tl(key: string, locale: string = 'en') {
  return t(key, null, { locale });
}

const MAX_ENTRIES = 150;
const PER_PAGE = 40;
const MARKERS_INDEX_OFFSET = 50;

const props = defineProps({
  searchPath: {
    type: String,
    default: 'homes-and-condos'
  },
  collection: {
    type: String as PropType<'properties' | 'projects'>,
    default: 'properties'
  },
  params: {
    type: Array as PropType<string[]>,
    default: () => []
  }
  // cardComponent: {
  //   type: Object as PropType<Component<CardProps>>
  // },
  // itemsWidth: {
  //   type: Number,
  //   default: 220
  // }
});

const searchingBuildings = computed(() => {
  return searchQueryParams.value.propertySubType?.includes('building');
});

const buildingsResults = computed(() => {
  if (cardResults.value.length === 0) {
    return searchingBuildings.value;
  }

  return (
    cardResults.value[0]?.projectStatus && props.collection === 'properties'
  );
});

// const itemsWidth = computed(() => {
//   return props.collection === 'projects' || buildingsResults.value ? 270 : 220;
// });

const propertiesFilters = ref(props.collection === 'properties');

const filtersMenu = ref(false);

const showMap = ref(false);

watch(showMap, () => {
  searchStore.setView(showMap.value ? 'map' : 'default');
});

const maxYears = computed(() => {
  return yearOptions.filter(
    year =>
      !searchQueryParams.value.yearFrom ||
      year >= searchQueryParams.value.yearFrom
  );
});

const maxAreas = computed(() => {
  return areaOptions.filter(
    area =>
      !searchQueryParams.value.areaFrom ||
      area.value >= searchQueryParams.value.areaFrom
  );
});

const maxCondoCost = computed(() => {
  return condoCostOptions.filter(
    cost =>
      !searchQueryParams.value.condoCostFrom ||
      cost.value >= searchQueryParams.value.condoCostFrom
  );
});

const prices = computed(() => {
  return searchQueryParams.value.buyType === 'RNT' ? rentPrices : buyPrices;
});

const maxPrices = computed(() => {
  const prices =
    searchQueryParams.value.buyType === 'RNT' ? rentPrices : buyPrices;

  return prices.filter(
    price =>
      !searchQueryParams.value.priceFrom ||
      price.value >= searchQueryParams.value.priceFrom
  );
});

const bedroomsOptions = Array.from({ length: 5 }, (_, i) => i + 1).map(i => ({
  text: i.toString(),
  value: i
}));

const maxBedroomsOptions = computed(() => {
  return bedroomsOptions.filter(
    option =>
      !searchQueryParams.value.bedroomsFrom ||
      option.value >= searchQueryParams.value.bedroomsFrom
  );
});

const bathsOptions = Array.from({ length: 5 }, (_, i) => i + 1).map(i => ({
  text: i.toString(),
  value: i
}));

const maxBathsOptions = computed(() => {
  return bathsOptions.filter(
    bath =>
      !searchQueryParams.value.bathroomsFrom ||
      bath.value >= searchQueryParams.value.bathroomsFrom
  );
});

function closeDropdown(name: string) {
  if (typeof document !== 'undefined') {
    const container = document.getElementById(`popover-${name}`);
    if (container) {
      container.dispatchEvent(new Event('mousedown'));
      container.dispatchEvent(new Event('click'));
      container.click();
    }
  }
}

const {
  locationsPromise,
  getCountyOptions,
  getCityOptions,
  getZipcodeOptions
} = useLocations();

const countyOptions = ref<CountyOption[]>([]);

function updateCountyOptions() {
  countyOptions.value = [
    {
      text: t('search.allCounties'),
      value: 'constempty',
      name: '',
      cities: []
    },
    ...getCountyOptions()
  ];
}

const cityOptions = ref<CityOption[]>([]);

const allCityOptions = computed<CityOption[]>(() => {
  return [
    {
      text: t('search.allCities'),
      value: 'constempty',
      county: null,
      zipcodes: []
    },
    ...getCityOptions()
  ];
});

function updateCityOptions() {
  cityOptions.value = [
    {
      text: t('search.allCities'),
      name: t('search.allCities'),
      value: 'constempty',
      county: null,
      zipcodes: []
    },
    ...getCityOptions(
      selectedCounty.value?.value === 'constempty' ? null : selectedCounty.value
    )
  ];
}

const zipcodeOptions = ref<ZipcodeOption[]>([]);

const allZipcodeOptions = computed<ZipcodeOption[]>(() => {
  return [
    {
      text: t('search.allZipcodes'),
      value: 'constempty',
      county: null,
      city: null
    },
    ...getZipcodeOptions()
  ];
});

function updateZipcodeOptions() {
  zipcodeOptions.value = [
    {
      text: t('search.allZipcodes'),
      value: 'constempty',
      county: null,
      city: null
    },
    ...getZipcodeOptions(
      selectedCity.value?.value === 'constempty' ? null : selectedCity.value,
      selectedCounty.value?.value === 'constempty' ? null : selectedCounty.value
    )
  ];
}

const selectedCounty = computed<CountyOption | null>(() => {
  return searchQueryParams.value.county
    ? countyOptions.value.find(
        ({ value }) => value === searchQueryParams.value.county
      )
    : null;
});

const selectedCity = computed<CityOption | null>(() => {
  return searchQueryParams.value.city
    ? cityOptions.value.find(
        ({ value }) => value === searchQueryParams.value.city
      )
    : null;
});

function setCounty(
  option: CountyOption | string | null,
  queryObject: SearchParams | null = null
) {
  if (typeof option === 'string') {
    option = countyOptions.value.find(
      o =>
        o.value.toLowerCase().replace('-', ' ') ===
        option.toLowerCase().replace('-', ' ')
    );
  } else if (option?.value) {
    const fixedOption = countyOptions.value.find(
      o =>
        o.value.toLowerCase().replace('-', ' ') ===
        option.value.toLowerCase().replace('-', ' ')
    );

    if (fixedOption) {
      option = fixedOption;
    }
  }

  let set = false;

  if (!queryObject) {
    queryObject = { ...searchQueryParams.value };
    set = true;
  }

  if (option?.value === 'constempty') {
    queryObject.county = 'constempty';
    queryObject.city = '';
    queryObject.zipcode = '';
    queryObject.neighborhood = '';
  } else if (!option?.value) {
    queryObject.county = '';
    queryObject.city = '';
    queryObject.zipcode = '';
    queryObject.neighborhood = '';
  } else {
    if (
      queryObject.city &&
      !option.cities.find(({ name }) => name === queryObject.city)
    ) {
      queryObject.city = '';
      if (queryObject.zipcode) {
        queryObject.zipcode = '';
      }
    }

    queryObject.county = option.value;
    locationJustChanged.value = true;
    console.log('[DEBUG] Ignoring bounds in next search');
  }

  if (set) {
    searchQueryParams.value = queryObject;
  }

  return queryObject;
}

function setCity(
  option: CityOption | string | null,
  queryObject: SearchParams | null = null
) {
  if (typeof option === 'string') {
    option = allCityOptions.value.find(
      o =>
        o.value.toLowerCase().replace('-', ' ') ===
        option.toLowerCase().replace('-', ' ')
    );
  } else if (option.value) {
    const fixedOption = allCityOptions.value.find(
      o =>
        o.value.toLowerCase().replace('-', ' ') ===
        option.value.toLowerCase().replace('-', ' ')
    );

    if (fixedOption) {
      option = fixedOption;
    }
  }

  let set = false;

  if (!queryObject) {
    queryObject = { ...searchQueryParams.value };
    set = true;
  }

  if (option?.value === 'constempty') {
    queryObject.county = queryObject.county || 'constempty';
    queryObject.city = 'constempty';
    queryObject.zipcode = '';
    queryObject.neighborhood = '';
  } else if (!option?.value) {
    queryObject.city = '';
    queryObject.zipcode = '';
    queryObject.neighborhood = '';
  } else {
    if (
      queryObject.zipcode &&
      !option.zipcodes.find(zipcode => zipcode === queryObject.zipcode)
    ) {
      queryObject.zipcode = '';
    }

    if (queryObject.county !== option.county) {
      queryObject.county = option.county;
    }

    queryObject.city = option.value;
    locationJustChanged.value = true;
    console.log('[DEBUG] Ignoring bounds in next search');
  }

  if (set) {
    searchQueryParams.value = queryObject;
  }

  return queryObject;
}

function setZipcode(
  option: ZipcodeOption | string | number | null,
  queryObject: SearchParams | null = null
) {
  let set = false;

  if (!queryObject) {
    queryObject = { ...searchQueryParams.value };
    set = true;
  }

  if (typeof option === 'string' || typeof option === 'number') {
    if (queryObject.city) {
      option =
        allZipcodeOptions.value.find(
          o => o.value === option.toString() && o.city === queryObject.city
        ) || option;
    }

    if (typeof option === 'string' || typeof option === 'number') {
      option = allZipcodeOptions.value.find(o => o.value === option.toString());
    }
  } else if (option.value) {
    if (queryObject.city) {
      option =
        allZipcodeOptions.value.find(
          o =>
            o.value === option.value.toString() && o.city === queryObject.city
        ) || option;
    }

    if (typeof option === 'string' || typeof option === 'number') {
      option = allZipcodeOptions.value.find(
        o => o.value === option.value.toString()
      );
    }
  }

  if (option?.value === 'constempty') {
    queryObject.county = queryObject.county || 'constempty';
    queryObject.city = queryObject.city || 'constempty';
    queryObject.zipcode = 'constempty';
    queryObject.neighborhood = '';
  } else if (!option?.value) {
    queryObject.zipcode = '';
    queryObject.neighborhood = '';
  } else {
    if (queryObject.county !== option.county) {
      queryObject.county = option.county;
    }

    if (queryObject.city !== option.city) {
      queryObject.city = option.city;
    }

    queryObject.zipcode = option.value;
    locationJustChanged.value = true;
    console.log('[DEBUG] Ignoring bounds in next search');
  }

  if (set) {
    searchQueryParams.value = queryObject;
  }

  return queryObject;
}

function setNeighborhood(
  option:
    | {
        value: string;
        county: string;
        city: string;
        zipcode: string;
      }
    | string,
  queryObject: SearchParams | null = null
) {
  if (typeof option === 'string') {
    option = loadedNeighborhoodOptions.value.find(
      o => o.value.toLowerCase() === (option as string).toLowerCase()
    ) || {
      value: option as string,
      county: '',
      city: '',
      zipcode: ''
    };
  } else if (
    typeof option === 'object' &&
    !option.value.county &&
    !option.value.city &&
    !option.value.zipcode
  ) {
    option =
      loadedNeighborhoodOptions.value.find(
        o => o.value.toLowerCase() === option.value.toLowerCase()
      ) || option;
  }

  let set = false;

  if (!queryObject) {
    queryObject = { ...searchQueryParams.value };
    set = true;
  }

  if (option?.value === 'constempty') {
    queryObject.county = queryObject.county || 'constempty';
    queryObject.city = queryObject.city || 'constempty';
    queryObject.zipcode = queryObject.zipcode || 'constempty';
    queryObject.neighborhood = '';
  } else if (!option?.value) {
    queryObject.neighborhood = '';
  } else {
    if (option.county && queryObject.county !== option.county) {
      queryObject.county = option.county;
    }

    if (option.city && queryObject.city !== option.city) {
      queryObject.city = option.city;
    }

    if (option.zipcode && queryObject.zipcode !== option.zipcode) {
      queryObject.zipcode = option.zipcode;
    }

    queryObject.neighborhood = option.value;
    locationJustChanged.value = true;
    console.log('[DEBUG] Ignoring bounds in next search');
  }

  if (set) {
    searchQueryParams.value = queryObject;
  }

  return queryObject;
}

const locationJustChanged = ref<boolean | null>(null);
const updatedValues = ref(false);

const page = ref(1);

interface Bounds {
  left: number | null;
  top: number | null;
  right: number | null;
  bottom: number | null;
  county?: string;
  city?: string;
  zipcode?: string;
}

const bounds = ref<Bounds>(
  (process.client &&
    props.collection === 'properties' &&
    searchStore.bounds) || {
    left: null,
    top: null,
    right: null,
    bottom: null
  }
);

const firstRequestDone = ref(false);

const firstBoundUpdateDone = ref(false);

const totalEntries = ref(0);

const baseResult = ref(null) as Ref<QueryResponse<DocumentResponse> | null>;

const markerResults = computed(() => {
  return baseResult.value?.hits.map(hit => hit.document);
});

/*
 * Index from the markers array to start slicing to get the cards array
 */
const sliceStartIndex = ref(0);

const totalEntriesInBounds = computed(() => {
  return baseResult.value?.found || baseResult.value?.found === 0
    ? baseResult.value.found
    : 0;
});

const omitBounds = ref(false);

const showAllProjects = computed(() => {
  return (
    props.collection === 'projects' && searchQueryParams.value.sortBy === 'city'
  );
});

const perPage = computed(() => {
  return showAllProjects.value ? MAX_ENTRIES : PER_PAGE;
});

const totalPages = computed(() => {
  return props.collection === 'projects'
    ? Math.ceil(totalEntries.value / perPage.value) || 1
    : Math.ceil(totalEntriesInBounds.value / perPage.value) || 1;
});

const {
  formatProperty,
  setFavorite,
  updateFavorites,
  getProjectLink,
  getUsedProjectLink,
  getPropertyLink,
  favorites
} = useProperties();

const clickedPin = ref('');
const hoveredItem = ref('');

const authModalPromise = ref(null) as Ref<ExternalPromise<void> | null>;

const { addToast } = useToasts();

async function setPropertyAsFavorite(property: CardProps, value: boolean) {
  let check = false;

  if (!isAuthenticated.value) {
    check = true;
    authModalPromise.value = new ExternalPromise<void>();
    await authModalPromise.value;
    authModalPromise.value = null;
    if (!isAuthenticated.value) {
      return;
    }
  }

  await setFavorite(property.id, value, check);
  triggerEvent('pe23281212_property_save', {
    saved: property.to
  });

  if (value) {
    addToast({
      title: t('property.propertySaved'),
      type: 'success'
    });
  } else {
    addToast({
      title: t('property.propertyRemoved'),
      type: 'info'
    });
  }
}

const cardResults = computed(() => {
  return (markerResults.value || []).slice(
    sliceStartIndex.value,
    sliceStartIndex.value + perPage.value
  );
});

const cardsInPage = ref([]) as Ref<CardProps[]>;
const cardsUpdated = ref(false);

function updateCardsInPage() {
  let assign = cardsInPage.value.length !== cardResults.value.length;

  if (!assign) {
    const cardsInPageString = cardsInPage.value
      .map(card => `${card.id}-${card.images?.length}`)
      .join(',');

    const cardResultsString = cardResults.value
      .map(
        result => `${result.id}-${cardsData.value[result.id]?.images?.length}`
      )
      .join(',');

    if (cardsInPageString !== cardResultsString) {
      assign = true;
    }
  }

  if (!assign) {
    cardsUpdated.value = true;
    return;
  }

  cardsInPage.value = cardResults.value.filter(Boolean).map(result => {
    // use data from cardsData
    const data: CardProps | {} = cardsData.value[result.id] || {};
    const item = {
      ...result,
      ...data
    };

    if (!cardsData.value[result.id]) {
      delete item.to;
    }

    return item as CardProps;
  });
  cardsUpdated.value = true;
}

const categorizedCards = computed(() => {
  if (
    props.collection === 'projects' &&
    searchQueryParams.value.sortBy === 'city'
  ) {
    const categories = (cardsInPage.value as ProjectCardProps[]).reduce(
      (acc, card) => {
        const city = card.city || t('search.otherCities');
        if (!acc[city]) {
          acc[city] = {
            title: city,
            cards: []
          };
        }
        acc[city].cards.push(card);
        return acc;
      },
      {} as Record<string, { title: string; cards: ProjectCardProps[] }>
    );

    const categoriesArray = Object.values(categories);

    categoriesArray.sort((a, b) => {
      if (a.title === t('search.otherCities')) {
        return 1;
      }
      if (b.title === t('search.otherCities')) {
        return -1;
      }
      return a.title.localeCompare(b.title);
    });

    return categoriesArray;
  }

  return [
    {
      title: '',
      cards: cardsInPage.value
    }
  ];
});

const searchSaved = ref(false);

watch(
  () => [isAuthenticated.value, cardResults.value],
  async () => {
    if (!isAuthenticated.value) {
      searchSaved.value = false;
      return;
    }

    await updateFavorites(cardResults.value.map(value => value.id));
  }
);

watch(markerResults, () => {
  nextTick(() => {
    if (locationJustChanged.value || updatedValues.value) {
      // only reset scroll when location or a value is changed
      updatedValues.value = false;
      window.scrollTo({
        top: 0
      });
    }
    updateCardsData();
  });
});

watch(
  page,
  () => {
    debouncedUpdatePathParameters();
  },
  {
    flush: 'post'
  }
);

watch(page, () => {
  debouncedTriggerSearch();
  window.scrollTo({
    top: 0
  });
});

watch(cardResults, () => {
  updateCardsInPage();
  updateCardsData();
});

watch(clickedPin, () => updateCardsData([clickedPin.value]));

watch(isAuthenticated, () => {
  if (isAuthenticated.value && authModalPromise.value) {
    authModalPromise.value.resolve();
    authModalPromise.value = null;
  }
});

const mapDefault = ref(null);
const mapProjects = ref(null);

const mapMarkers = computed(() => {
  const m = markerResults.value?.map((hit, index) => {
    const price: number =
      'listPrice' in hit
        ? (hit?.listPrice as number)
        : 'minPrice' in hit
        ? (hit?.minPrice as number)
        : 0;
    return {
      ...(formatProperty(
        hit as DocumentResponse,
        index
      ) as unknown as CardProps),
      id: hit.id,
      shortPrice: price ? formatShortPrice(price, 0, locale.value) : 'PFS',
      position: {
        lat: hit.geopoint[0],
        lng: hit.geopoint[1]
      }
    };
  });

  return m?.length ? m : null;
});

type CardPropsExtended = PropertyCardPropsExtended | ProjectCardPropsExtended;

const shownMarkers = ref<CardPropsExtended[] | null>(null);

const updateMarkersDebounced = debounce(() => {
  const shownMarkersCopy = shownMarkers.value?.map(({ id }) => id);
  const mapMarkersCopy = mapMarkers.value?.map(({ id }) => id);

  shownMarkersCopy?.sort();
  mapMarkersCopy?.sort();

  if (JSON.stringify(mapMarkersCopy) === JSON.stringify(shownMarkersCopy)) {
    return;
  }

  shownMarkers.value = [];

  nextTick(() => {
    shownMarkers.value = mapMarkers.value;
    updateMap();
  });
}, 250);

watch(mapMarkers, updateMarkersDebounced);

async function updateMap() {
  const map =
    props.collection === 'projects' ? mapProjects.value : mapDefault.value;

  if (
    !map ||
    typeof map.updateMap !== 'function' ||
    typeof window === 'undefined'
  ) {
    return;
  }

  const locationChanged =
    (bounds.value?.county &&
      bounds.value?.county !== searchQueryParams.value.county) ||
    (bounds.value?.city &&
      bounds.value?.city !== searchQueryParams.value.city) ||
    (bounds.value?.zipcode &&
      bounds.value?.zipcode !== searchQueryParams.value.zipcode) ||
    locationJustChanged.value;

  const fitBounds = !window.goingBack && locationChanged;

  const updated = await map.updateMap(fitBounds);

  if (updated !== null || props.collection === 'projects') {
    locationJustChanged.value = null;
    window.goingBack = false;
  }

  return updated;
}

interface Point {
  lat: number;
  lng: number;
}

function pushCustomPolygon(paths: Point[][]) {
  if (!paths?.length || (paths.length === 1 && !paths?.[0]?.length)) {
    return;
  }
  customPolygon.value = multiPolygon.value || [];
  customPolygon.value.push(...paths);
  searchStore.setCustomPolygon(customPolygon.value);
}

function resetCustomPolygon() {
  customPolygon.value = null;
  searchStore.setCustomPolygon(null);
}

function clearPolygon() {
  customPolygon.value = null;
  setCounty(null);
}

watch(
  markerResults,
  () => {
    updateMap();
  },
  {
    immediate: true,
    deep: true,
    flush: 'post'
  }
);

const neighborhoodOptions = computed(() => {
  return [
    {
      value: 'constempty',
      text: t('search.allNeighborhoods'),
      county: '',
      city: '',
      zipcode: ''
    },
    ...loadedNeighborhoodOptions.value
  ];
});

const searchSummary = computed(() => {
  return getSearchSummary({
    ...searchQueryParams.value,
    buyType:
      props.searchPath === tl('routes.projects')
        ? 'NEW'
        : searchQueryParams.value.buyType,
    location:
      searchQueryLocation.value?.type === 'geolocation'
        ? 'constgeolocation'
        : searchQueryValue.value
  });
});

const formatNumber = Intl.NumberFormat(language.value).format;

const resultTitle = computed(() => {
  let str = '';

  /*
   * If there are results, show the range of results in the current page
   */
  const from = (page.value - 1) * perPage.value + 1;
  const to = Math.min(page.value * perPage.value, totalEntriesInBounds.value);

  if (from && to) {
    str = `${formatNumber(from)}-${formatNumber(to)}`;
  }

  if (totalEntries.value) {
    const total = t('search.nResults', [formatNumber(totalEntries.value)]);

    if (str) {
      str = t('connectors.of', [str, total]);
    } else {
      str = total;
    }
  }

  return str;
});

const propertyTypeButtonText = computed(() => {
  const propertyTypes = searchQueryParams.value?.propertyType?.length
    ? searchQueryParams.value.propertyType
    : [];

  const propertyTypeText = joinAnd(
    propertyTypesOptions
      .map(option =>
        (propertyTypes as string[]).includes(option.value) ? option.text : null
      )
      .filter(Boolean),
    t
  );

  return propertyTypeText || t('search.selectPropertyType');
});

const propertySubTypeButtonText = computed(() => {
  const defaultText =
    searchQueryParams.value.propertyType?.length !== 1
      ? t('search.selectPropertySubTypes')
      : searchQueryParams.value.propertyType[0] === 'house'
      ? t('search.selectHouseType')
      : t('search.selectCondoType');

  if (!searchQueryParams.value.propertySubType?.length) {
    return defaultText;
  }

  if (searchQueryParams.value.propertySubType.length) {
    return (
      joinAnd(
        propertySubTypesOptions
          .map(option =>
            (searchQueryParams.value.propertySubType as string[]).includes(
              option.value
            )
              ? option.text
              : null
          )
          .filter(Boolean),
        t
      ) || defaultText
    );
  }

  return defaultText;
});

const priceButtonText = computed(() => {
  return (
    getPriceButtonText(searchQueryParams.value, locale.value) ||
    t('search.price')
  );
});

const bedroomsAndBathroomsButtonText = computed(() => {
  return (
    getBedroomsAndBathroomsButtonText(searchQueryParams.value, t) ||
    t('search.bedroomsAndBathrooms')
  );
});

//

const searchQueryString = ref('');

const validSearchQueryString = computed(() => {
  const locationValues = [
    searchQueryParams.value.county,
    searchQueryParams.value.city,
    searchQueryParams.value.zipcode
  ].filter(Boolean);

  if (
    locationValues.find(
      value => searchQueryString.value.toLowerCase() === value.toLowerCase()
    )
  ) {
    return '';
  }

  return searchQueryString.value;
});

const searchQueryValue = computed(() => {
  if (!searchQueryLocation.value) {
    return validSearchQueryString.value || '';
  }
  if (['location', 'geolocation'].includes(searchQueryLocation.value.type)) {
    return searchQueryLocation.value.value || '';
  }
  return '';
});

function getSearchParameters(
  tLocal: (v: string) => string,
  options: {
    omit?: string[];
  } = {}
) {
  const query: Record<string, QueryValue> = {
    ...searchQueryParams.value
  };

  if (page.value > 1) {
    query.page = page.value.toString();
  }

  if (!query.buyType && props.collection === 'properties') {
    query.buyType = 'RES';
  }

  if (options?.omit) {
    options.omit.forEach(key => {
      delete query[key];
    });
  }

  const params = _getSearchParameters(
    {
      isProject: props.collection === 'projects',
      ...query
    },
    tLocal
  );

  if (t === tLocal) {
    if (params.length && process.client) {
      if (props.collection === 'projects') {
        searchStore.setProjectSearchParams(params);
      } else {
        searchStore.setPropertiesSearchParams(params);
      }
    }
  }

  return params;
}

const searchParameters = computed(() => {
  return getSearchParameters(t);
});

function updatePathParameters(push = false) {
  const searchPath =
    props.searchPath === tl('routes.projects')
      ? t('routes.projects')
      : t('routes.properties');

  const newPath = localePath(
    `/${searchPath}/${searchParameters.value.join('/')}`
  );

  const isAboutToUnmount = !document.getElementById('search-results');

  if (isAboutToUnmount) {
    console.log(
      '[PARAMETERS UPDATE] Ignoring path update because the component is about to unmount'
    );
    return;
  }

  if (newPath !== location.pathname && newPath !== location.href) {
    console.log(
      `Updating path from '${
        location.pathname || location.href
      }' to '${newPath}'`
    );
    if (push) {
      history.pushState(history.state, '', newPath);
    } else {
      history.replaceState(history.state, '', newPath);
    }
  }
}

const debouncedUpdatePathParameters = debounce(updatePathParameters, 25);

const valuesUpdatedForFirstTime = ref(false);

function updateValuesFromPathParameters(update = true) {
  const urlParams = [...props.params];

  const isProject = props.searchPath !== tl('routes.properties');

  // legacy two param zipcode

  const legacyZipcodeIndex = urlParams.findIndex(
    (param: string) => param === 'codigo-postal'
  );

  const zipCodeValue =
    legacyZipcodeIndex >= 0 ? urlParams[legacyZipcodeIndex + 1] : null;

  if (zipCodeValue && !isNaN(+zipCodeValue)) {
    urlParams.splice(legacyZipcodeIndex, 2);
    urlParams.push(`${zipCodeValue}-zipcode`);
  }

  // legacy two param city

  const legacyCityIndex = urlParams.findIndex(
    (param: string) => param === 'ciudad'
  );

  const cityValue =
    legacyCityIndex >= 0 ? urlParams[legacyCityIndex + 1] : null;

  if (
    cityValue &&
    !getParam(cityValue, legacyCityIndex + 1, isProject, false)
  ) {
    urlParams.splice(legacyCityIndex, 2);
    urlParams.push(`${cityValue}-city`);
  }

  // legacy two param county

  const legacyCountyIndex = urlParams.findIndex(
    (param: string) => param === 'condado'
  );

  const countyValue =
    legacyCountyIndex >= 0 ? urlParams[legacyCountyIndex + 1] : null;

  if (
    countyValue &&
    !getParam(countyValue, legacyCountyIndex + 1, isProject, false)
  ) {
    urlParams.splice(legacyCountyIndex, 2);
    urlParams.push(`${countyValue}-county`);
  }

  //

  let newSearchQueryParams: SearchParams = {
    ...searchQueryParams.value
  };

  let newSearchQuery: SearchLocation | null = {
    text: searchQueryValue.value,
    value: searchQueryValue.value,
    type: 'location'
  };

  const params = urlParams
    .map((value, index) => getParam(value, index, isProject, false))
    .flat()
    .filter(Boolean);

  params.forEach(param => {
    if (!param || typeof param !== 'object') {
      console.error('Invalid param', param);
      return;
    }

    const { key, value } = param;

    switch (key) {
      case 'propertySubType':
      case 'propertyType':
        if (value) {
          newSearchQueryParams[key] = [value.toString()] as never;
        }
        break;
      case 'projectType':
      case 'buyType':
      case 'sortBy':
        if (value) {
          newSearchQueryParams[key] = value.toString() as never;
        }
        break;
      case 'bedroomsFrom':
      case 'bedroomsTo':
      case 'bathroomsFrom':
      case 'bathroomsTo':
      case 'priceFrom':
      case 'priceTo':
      case 'areaFrom':
      case 'areaTo':
      case 'yearFrom':
      case 'yearTo':
      case 'condoCostFrom':
      case 'condoCostTo':
        value && (newSearchQueryParams[key] = parseInt(value.toString()));
        break;
      case 'dock':
      case 'shortTermLease':
      case 'excludePendingAndUnderContract':
      case 'inListing':
        if (value) {
          newSearchQueryParams[key] = Boolean(value);
        }
        break;
      case 'location':
        if (value === 'constempty') {
          newSearchQuery = {
            text: '',
            value: '',
            type: 'location'
          };
        } else {
          newSearchQuery = {
            text: value.toString(),
            value: value.toString(),
            type: 'location'
          };
        }
        break;

      case 'neighborhood':
        if (value) {
          newSearchQueryParams.neighborhood = value.toString();
          newSearchQuery = {
            text: value.toString(),
            value: value.toString(),
            type: 'neighborhood'
          };
        }
        break;
      case 'zipcode':
        if (value) {
          newSearchQueryParams = setZipcode(value, newSearchQueryParams);
        }
        break;
      case 'city':
        if (value) {
          newSearchQueryParams = setCity(
            value.toString(),
            newSearchQueryParams
          );
        }
        break;
      case 'county':
        if (value) {
          newSearchQueryParams = setCounty(
            value.toString(),
            newSearchQueryParams
          );
        }
        break;
      case 'geolocation':
        if (value) {
          newSearchQueryParams[key] = value.toString();
          newSearchQuery = {
            text: value.toString(),
            value: value.toString(),
            type: key
          };
        }
        break;
      case 'page':
        page.value = parseInt(value.toString());
        break;
      default:
        newSearchQueryParams[key as keyof SearchParams] = paramToValue(
          value
        ) as never;
    }
  });

  // transform 'buy' to 'RES' using valuesMap
  // TODO: Use a property to generate the array of keys

  for (const key of [
    'buyType',
    'projectType',
    'propertyType',
    'propertySubType',
    'hopa',
    'sortBy'
  ] as const) {
    if (newSearchQueryParams[key]) {
      if (Array.isArray(newSearchQueryParams[key])) {
        newSearchQueryParams[key] = newSearchQueryParams[key].map(
          v => valuesMap[v as string] || v
        ) as never;
      } else {
        newSearchQueryParams[key] = valuesMap[
          newSearchQueryParams[key] as string
        ] as never;
      }
    }
  }

  if (
    newSearchQuery?.value &&
    searchQueryLocation.value?.value !== newSearchQuery?.value
  ) {
    searchQueryLocation.value = newSearchQuery;
  } else if (!newSearchQuery?.value) {
    searchQueryLocation.value = null;
  }

  if (!compareObjects(searchQueryParams.value, newSearchQueryParams)) {
    searchQueryParams.value = newSearchQueryParams;
  }

  updateBounds(bounds.value, update);

  valuesUpdatedForFirstTime.value = true;
}

const debouncedUpdateBoundsOnStore = debounce(() => {
  searchStore.setBounds(
    bounds.value
      ? {
          ...bounds.value,
          county: searchQueryParams.value.county,
          city: searchQueryParams.value.city,
          zipcode: searchQueryParams.value.zipcode
        }
      : null
  );
}, 100);

watch(bounds, () => {
  debouncedUpdateBoundsOnStore();
});

async function updateNeighborhoodOptions() {
  const query = {
    zipcode: searchQueryParams.value.zipcode,
    city: searchQueryParams.value.city,
    county: searchQueryParams.value.county
  };

  for (const key of Object.keys(query) as (keyof typeof query)[]) {
    if (query[key] === 'constempty') {
      query[key] = '';
    }
  }

  const enableSearch =
    !query.zipcode && !query.city && !query.county && neighborhoodsSearch.value;

  const queryParams = {
    q: enableSearch ? neighborhoodsSearch.value : '*',
    query_by: 'label',
    filter_by: [
      'meta.type:=neighborhood',
      // query.zipcode ? `meta.postal_code:=${query.zipcode}` : '',
      query.city ? `meta.city:=${query.city}` : '',
      query.county ? `meta.county:=${query.county}` : ''
    ]
      .filter(Boolean)
      .join(' && '),
    exclude_fields: 'meta.geojson,created_at,type_order',
    per_page: MAX_ENTRIES,
    highlight_fields: 'x',
    'x-typesense-user-id': user.value?.id || ''
  };

  const url =
    apiUrl +
    '/search/insta-search/?' +
    Object.entries(queryParams)
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');

  const response = await get<{
    data: {
      hits: {
        document: {
          label: string;
          meta: {
            county: string;
            city: string;
            postal_code: string;
          };
        };
      }[];
    };
  }>(url);

  const options = response.data.hits.map(hit => ({
    value: hit.document.label,
    text: hit.document.label,
    county: hit.document.meta.county,
    city: hit.document.meta.city,
    zipcode: hit.document.meta.postal_code
  }));

  options.sort();

  loadedNeighborhoodOptions.value = options;

  if (
    !searchQueryParams.value.neighborhood &&
    searchQueryLocation.value?.value
  ) {
    const found = neighborhoodOptions.value.find(
      n =>
        valueToParam(n.value) === valueToParam(searchQueryLocation.value.value)
    );
    if (found) {
      setNeighborhood(found);
    }
  }
}

const debouncedUpdateNeighborhoodOptions = debounce(
  updateNeighborhoodOptions,
  30
);

watch(
  neighborhoodsSearch,
  v => {
    if (
      v &&
      !searchQueryParams.value.zipcode &&
      !searchQueryParams.value.city &&
      !searchQueryParams.value.county
    )
      debouncedUpdateNeighborhoodOptions();
  },
  { immediate: true }
);

watch(
  searchQueryLocation,
  () => {
    if (firstRequestDone.value) {
      updatedValues.value = true;
      page.value = 1;
    }

    const types = ['geolocation', 'location'] as const;

    types
      .filter(t => t !== searchQueryLocation.value?.type)
      .forEach(t => {
        delete searchQueryParams.value[t];
      });

    if (searchQueryLocation.value?.type === 'zip_code') {
      setZipcode(searchQueryLocation.value.value);
    } else if (searchQueryLocation.value?.type === 'city') {
      setCity(searchQueryLocation.value.value);
    } else if (searchQueryLocation.value?.type === 'county') {
      setCounty(searchQueryLocation.value.value);
    } else if (searchQueryLocation.value?.type === 'neighborhood') {
      setNeighborhood({
        value: searchQueryLocation.value.value,
        county: searchQueryLocation.value.county,
        city: searchQueryLocation.value.city,
        zipcode: searchQueryLocation.value.zipcode
      });
    }

    nextTick().then(() => {
      debouncedTriggerSearch();
      debouncedUpdatePathParameters();
    });

    searchSaved.value = false;
  },
  { flush: 'post', deep: true }
);

function setBuyType(value: 'RES' | 'RNT' | 'SOL' | 'NEW') {
  if (value === 'NEW') {
    navigateTo(localePath(`/${t('routes.projects')}`));
  } else if (!value) {
    searchQueryParams.value.buyType = null;
    nextTick().then(() => {
      searchQueryParams.value.buyType = 'RES';
    });
  } else {
    if (searchQueryParams.value.propertySubType?.includes('building')) {
      delete searchQueryParams.value.propertySubType;
    }
    searchQueryParams.value.buyType = value;
  }
}

function updateSubTypes() {
  if (!searchQueryParams.value.propertySubType?.length) {
    return;
  }

  const newSubTypes = searchQueryParams.value.propertySubType.filter(v =>
    filteredSubTypesOptions.value.find(o => o.value === v)
  );
  if (newSubTypes.length !== searchQueryParams.value.propertySubType.length) {
    searchQueryParams.value.propertySubType = newSubTypes;
  }
}

function setPropertyType(value: ('apartment' | 'house')[]) {
  searchQueryParams.value.propertyType = value;
  updateSubTypes();
}

let previousSubType: PropertySubType[] = [];

function setSubType(value: PropertySubType[]) {
  previousSubType = searchQueryParams.value.propertySubType || [];

  if (value.includes('building') && !previousSubType.includes('building')) {
    if (previousSubType.join(',') !== 'building') {
      searchQueryParams.value.propertySubType = ['building'];
    }
  } else if (previousSubType.includes('building') && value.length > 1) {
    searchQueryParams.value.propertySubType = value.filter(
      v => v !== 'building'
    );
  } else if (previousSubType.join(',') !== value.join(',')) {
    searchQueryParams.value.propertySubType = value;
  }

  updateSubTypes();
}

function checkRangeFields(
  values: number[],
  from: keyof SearchParams,
  to: keyof SearchParams
) {
  if (
    searchQueryParams.value[from] &&
    !values.find(v => v === searchQueryParams.value[from])
  ) {
    searchQueryParams.value[from] = undefined as never;
  }

  if (
    searchQueryParams.value[to] &&
    !values.find(v => v === searchQueryParams.value[to])
  ) {
    searchQueryParams.value[to] = undefined as never;
  }

  if (
    searchQueryParams.value[to] &&
    (!searchQueryParams.value[from] ||
      !values.find(v => v === searchQueryParams.value[from])) &&
    searchQueryParams.value[from] !== values[0]
  ) {
    searchQueryParams.value[from] = (values?.[0] || undefined) as never;
  }

  if (searchQueryParams.value[from] && searchQueryParams.value[to]) {
    if (searchQueryParams.value[from] > searchQueryParams.value[to]) {
      searchQueryParams.value[to] = searchQueryParams.value[from] as never;
    }
  }
}

watch(
  searchQueryParams,
  () => {
    if (firstRequestDone.value && page.value !== 1) {
      page.value = 1;
    }
    checkRangeFields(
      prices.value.map(o => o.value),
      'priceFrom',
      'priceTo'
    );
    checkRangeFields(
      areaOptions.map(o => o.value),
      'areaFrom',
      'areaTo'
    );
    checkRangeFields(yearOptions, 'yearFrom', 'yearTo');
    checkRangeFields(
      condoCostOptions.map(o => o.value),
      'condoCostFrom',
      'condoCostTo'
    );

    if (searchQueryParams.value.propertySubType?.[0] === 'building') {
      if (searchQueryParams.value.buyType !== 'RES') {
        searchQueryParams.value.buyType = 'RES';
      }

      if (searchQueryParams.value.condoCostFrom) {
        delete searchQueryParams.value.condoCostFrom;
      }

      if (searchQueryParams.value.condoCostTo) {
        delete searchQueryParams.value.condoCostTo;
      }

      if (searchQueryParams.value.view) {
        delete searchQueryParams.value.view;
      }
    }

    if (
      !['RNT', 'RTD'].includes(searchQueryParams.value.buyType) &&
      searchQueryParams.value.shortTermLease
    ) {
      searchQueryParams.value.shortTermLease = false;
    }

    if (
      searchQueryParams.value.excludePendingAndUnderContract &&
      searchQueryParams.value.buyType !== 'RES'
    ) {
      searchQueryParams.value.excludePendingAndUnderContract = false;
    }

    debouncedTriggerSearch();
    debouncedUpdatePathParameters();
    setTimeout(() => {
      updateCityOptions();
      updateZipcodeOptions();
      updateNeighborhoodOptions();
      updatePolygon();
    }, 750);
    updatedValues.value = true;
    searchSaved.value = false;
  },
  { flush: 'post', deep: true }
);

let loadedPolygon: number | string = '';

function updatePolygon() {
  if (
    (!searchQueryParams.value.county ||
      searchQueryParams.value.county === 'constempty') &&
    (!searchQueryParams.value.city ||
      searchQueryParams.value.city === 'constempty') &&
    (!searchQueryParams.value.zipcode ||
      searchQueryParams.value.zipcode === 'constempty') &&
    (!searchQueryParams.value.neighborhood ||
      searchQueryParams.value.neighborhood === 'constempty')
  ) {
    polygonResponse.value = null;
    return;
  }

  if (
    searchQueryParams.value.neighborhood &&
    searchQueryParams.value.neighborhood !== 'constempty'
  ) {
    if (loadedPolygon === searchQueryParams.value.neighborhood) {
      return;
    }
    return loadPolygon(searchQueryParams.value.neighborhood, 'neighborhood');
  }

  if (
    searchQueryParams.value.zipcode &&
    searchQueryParams.value.zipcode !== 'constempty'
  ) {
    if (loadedPolygon === searchQueryParams.value.zipcode) {
      return;
    }
    return loadPolygon(searchQueryParams.value.zipcode, 'zipcode');
  }

  if (
    searchQueryParams.value.city &&
    searchQueryParams.value.city !== 'constempty'
  ) {
    if (loadedPolygon === searchQueryParams.value.city) {
      return;
    }
    return loadPolygon(searchQueryParams.value.city, 'city');
  }

  if (
    searchQueryParams.value.county &&
    searchQueryParams.value.county !== 'constempty'
  ) {
    if (loadedPolygon === searchQueryParams.value.county) {
      return;
    }
    return loadPolygon(searchQueryParams.value.county, 'county');
  }

  if (
    searchQueryParams.value.neighborhood &&
    searchQueryParams.value.neighborhood !== 'constempty'
  ) {
    if (loadedPolygon === searchQueryParams.value.neighborhood) {
      return;
    }
    return loadPolygon(searchQueryParams.value.neighborhood, 'neighborhood');
  }
}

function loadPolygon(
  value: string,
  type: 'zipcode' | 'city' | 'county' | 'neighborhood'
) {
  resetCustomPolygon();

  loadedPolygon = value;

  const queryParams = {
    q: '*',
    query_by: 'label',
    filter_by: [`meta.type:=${type}`, `label:=${value}`]
      .filter(Boolean)
      .join(' && '),
    include_fields: 'meta.geojson',
    per_page: 1,
    'x-typesense-user-id': user.value?.id || ''
  };

  const url =
    apiUrl +
    '/search/insta-search/?' +
    Object.entries(queryParams)
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');

  get<{
    data: {
      hits: {
        document: {
          meta: {
            geojson: PolygonResponse;
          };
        };
      }[];
    };
  }>(url).then(response => {
    if (response.data.hits.length) {
      polygonResponse.value = response.data.hits[0].document.meta.geojson;
    } else {
      polygonResponse.value = null;
    }
  });
}

function updateBounds(b: Bounds, update = true) {
  if (!compareObjects(b, bounds.value)) {
    bounds.value = b;

    window.scrollTo({
      top: 0
    });

    if (update) {
      triggerSearch();
      debouncedUpdatePathParameters(true);
    }
  }
}

const lastRequestUrl = ref('');

function getPolygonLocation(polygons: LatLngPolygon[]) {
  return (
    '(' +
    polygons
      .map(
        polygon =>
          `geopoint:(${polygon
            .flatMap((p: { lat: number; lng: number }) => [p.lat, p.lng])
            .join(', ')})`
      )
      .join(' || ') +
    ')'
  );
}

function getSearchQueryParams(
  options: {
    omit?: string[];
    replace?: Record<string, string | number | boolean | string[]>;
  } = {}
) {
  const collection = searchingBuildings.value ? 'projects' : props.collection;

  const location =
    searchQueryLocation.value?.type === 'geolocation'
      ? ''
      : searchQueryValue.value;

  const geolocation =
    searchQueryLocation.value?.type === 'geolocation'
      ? searchQueryLocation.value.value
      : '';

  delete searchQueryParams.value.geolocation;

  const omitBounds =
    (options?.omit || []).includes('bounds') ||
    !bounds.value?.left ||
    !bounds.value?.right ||
    !bounds.value?.top ||
    !bounds.value?.bottom;

  let searchParamsInput = {
    collection,
    location,
    geolocation,
    leftBound: omitBounds ? null : bounds.value?.left,
    rightBound: omitBounds ? null : bounds.value?.right,
    topBound: omitBounds ? null : bounds.value?.top,
    bottomBound: omitBounds ? null : bounds.value?.bottom,
    perPage: MAX_ENTRIES,
    projectStatus: searchingBuildings.value ? 'on-the-market' : undefined,
    filterBy: customPolygon.value?.length
      ? [getPolygonLocation(customPolygon.value)]
      : [],
    includeFields:
      collection === 'properties'
        ? 'geopoint,id,listPrice,mlsId'
        : 'geopoint,id,minPrice,projectStatus,projectName,subType',
    ...searchQueryParams.value
  };

  if (options.omit) {
    options.omit.forEach(key => {
      if (key in searchParamsInput) {
        delete searchParamsInput[key as keyof typeof searchParamsInput];
      }
    });
  }

  if (options.replace && Object.keys(options.replace).length) {
    searchParamsInput = {
      ...searchParamsInput,
      ...options.replace
    };
  }

  const queryParams = createSearchParams(searchParamsInput);

  return Object.entries(queryParams)
    .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
    .join('&');
}

let lastTotalUrl = '';

let firstLoad = true;

async function triggerSearch(retrigger = true) {
  if (!valuesUpdatedForFirstTime.value) {
    updateValuesFromPathParameters();
    if (retrigger) triggerSearch(false);
    return;
  }

  const path =
    props.collection === 'projects' || searchingBuildings.value
      ? 'search/projects'
      : 'search';

  const totalUrl = `${apiUrl}/${path}/?${getSearchQueryParams({
    omit: ['bounds'],
    replace: {
      includeFields: 'id',
      perPage: 1
    }
  })}`;

  const boundsLocationChanged =
    (bounds.value?.county &&
      searchQueryParams.value.county &&
      bounds.value?.county !== searchQueryParams.value.county) ||
    (bounds.value?.city &&
      searchQueryParams.value.city &&
      bounds.value?.city !== searchQueryParams.value.city) ||
    (bounds.value?.zipcode &&
      searchQueryParams.value.zipcode &&
      bounds.value?.zipcode !== searchQueryParams.value.zipcode);

  const goingBack = typeof window !== 'undefined' && window.goingBack;

  let omitBoundsInThisSearch =
    boundsLocationChanged || (firstLoad && !goingBack);

  if (totalUrl !== lastTotalUrl) {
    lastTotalUrl = totalUrl;
    get<{ data: { found: number } }>(totalUrl).then(response => {
      totalEntries.value = response.data?.found || 0;
      omitBounds.value = totalEntries.value <= MAX_ENTRIES;
    });
  } else {
    omitBoundsInThisSearch = omitBounds.value;
  }

  const startIndex = (page.value - 1) * perPage.value;
  const markerStartIndex = Math.max(startIndex - MARKERS_INDEX_OFFSET, 0);

  sliceStartIndex.value = startIndex - markerStartIndex;

  const queryParams = getSearchQueryParams({
    omit: omitBoundsInThisSearch ? ['bounds'] : null,
    replace: {
      perPage: MAX_ENTRIES,
      offset: markerStartIndex
    }
  });

  const url = `${apiUrl}/${path}/?${queryParams}`;

  if (url === lastRequestUrl.value) {
    return;
  }

  firstLoad = false;

  lastRequestUrl.value = url;

  cardsUpdated.value = false;

  baseResult.value = (await get<{ data: unknown }>(url))
    .data as typeof baseResult.value;

  firstRequestDone.value = true;

  if (!customPolygon.value?.length && usingPolygon.value) {
    nextTick(() => {
      usingPolygon.value = false;
    });
  }
}

const debouncedTriggerSearch = debounce(triggerSearch, 25);

watch(customPolygon, debouncedTriggerSearch, { deep: true });

const cardsData = ref<Record<string | number, CardProps>>({});

async function updateCardsData(idsInput: string[] | null = null) {
  let ids: string[] =
    idsInput || cardResults.value?.map(item => item?.id) || [];

  if (!ids.length) {
    return;
  }

  // limit cardsData to 1000 items

  if (cardsData.value && Object.keys(cardsData.value).length > 750) {
    const loaded = Object.keys(cardsData.value);
    const alreadyLoadedIds = loaded.filter(key => ids.includes(key));
    const notRequiredIds = loaded.filter(key => !ids.includes(key));

    const newCardsDataIds = [...alreadyLoadedIds, ...notRequiredIds];

    newCardsDataIds.splice(0, Math.max(alreadyLoadedIds.length + 10, 750));

    cardsData.value = Object.fromEntries(
      Object.entries(cardsData.value).filter(([key]) =>
        newCardsDataIds.includes(key)
      )
    );
  }

  ids = ids.filter(id => id && !cardsData.value[id]);

  if (!ids.length) {
    console.log('[DEBUG] Trying to request no new cards data');
    cardsUpdated.value = true;
    return;
  }

  const path =
    props.collection === 'projects' || searchingBuildings.value
      ? 'search/projects'
      : 'search';

  const collection = searchingBuildings.value ? 'projects' : props.collection;

  const filterBy = [`id:[${ids.join(',')}]`];

  const queryParams = new URLSearchParams({
    ...createSearchParams({
      collection,
      perPage: perPage.value,
      filterBy,
      omitPriceFilter: true,
      propertySubType: searchingBuildings.value ? ['building'] : [],
      includeFields:
        collection === 'properties'
          ? 'bathsFull,bathsHalf,bedrooms,city,county,full,geopoint,id,listPrice,mlsId,photos,postalCode,propertyArea,state,streetName,subType,type'
          : 'address,city,county,id,geopoint,minPrice,photos,projectName,projectStatus,subType,yearCompletion'
    })
  });

  const url = `${apiUrl}/${path}/?${queryParams}`;

  const response = await get<{
    data: { hits: { document: DocumentResponse }[] };
  }>(url);

  response.data.hits.forEach(hit => {
    cardsData.value[hit.document.id] = formatProperty(hit.document);
  });

  nextTick(() => {
    updateCardsInPage();
  });
}

function resetFilters(values: string[] = []) {
  if (!values?.length) {
    searchQueryLocation.value = null;
    searchQueryParams.value = {};
  } else {
    const newParams = { ...searchQueryParams.value };

    for (const key of Object.keys(newParams) as (keyof typeof newParams)[]) {
      if (values.includes(key)) {
        delete newParams[key];
      }
    }

    if (
      values.includes('zipcode') ||
      values.includes('city') ||
      values.includes('county') ||
      values.includes('neighborhood')
    ) {
      searchQueryLocation.value = null;
    }

    searchQueryParams.value = newParams;
  }
  resetCustomPolygon();
  updatePathParameters();
}

const {
  areaOptions,
  condoCostOptions,
  hopaOptions,
  yearOptions,
  rentPrices,
  buyPrices,
  sortByOptions,
  sortProjectsByOptions,
  buyTypeOptions,
  propertyTypesOptions,
  propertySubTypesOptions,
  projectTypeOptions,
  viewOptions,
  valueToParam,
  paramToValue,
  saveSearch: _saveSearch,
  getSearchParameters: _getSearchParameters,
  getBreadcrumbs,
  getSearchSummary,
  createSearchParams,
  getParam
} = useSearch();

const viewsResult = ref<string[] | null>(null);

const loadingViews = ref<Boolean>(false);

const filteredViewOptions = computed<(typeof viewOptions)[number]>(() => {
  if (!viewsResult.value) {
    return viewOptions;
  }

  if (!viewsResult.value.length) {
    return [];
  }

  return viewOptions.filter((option, index) => {
    if (index === 0) {
      return true;
    }
    return [option.es, option.en, option['value-es'], option['value-en']]
      .filter(Boolean)
      .some(optionValue => viewsResult.value.includes(optionValue));
  });
});

let lastViewsRequestUrl = '';

async function updateViews() {
  const path =
    props.collection === 'projects' || searchingBuildings.value
      ? 'search/projects'
      : 'search';

  const queryParamsObject = { ...searchQueryParams.value };

  delete queryParamsObject.view;

  const collection = searchingBuildings.value ? 'projects' : props.collection;

  if (collection === 'projects') {
    return;
  }

  const queryParams = new URLSearchParams({
    ...createSearchParams({
      collection,
      location: searchQueryValue.value,
      page: 0,
      perPage: MAX_ENTRIES,
      projectStatus: searchingBuildings.value ? 'on-the-market' : undefined,
      includeFields: 'id',
      ...queryParamsObject
    })
  });

  const newViewsRequestUrl = `${apiUrl}/${path}/?${queryParams}&group_by=view&group_limit=1`;

  if (newViewsRequestUrl === lastViewsRequestUrl) {
    return;
  }

  lastViewsRequestUrl = newViewsRequestUrl;

  viewsResult.value = null;

  loadingViews.value = true;

  const viewsResponse = (
    await get<{
      data: GroupQueryResponse<PropertyDocumentResponse<'id'>, string[]>;
    }>(newViewsRequestUrl)
  ).data;

  const possibleViews = viewsResponse?.grouped_hits.flatMap(group => {
    return group.group_key?.[0];
  });

  viewsResult.value = [
    ...new Set([...new Set(possibleViews)].map(v => v.trim()))
  ];

  loadingViews.value = false;
}

const projectBuyType = ref(
  props.searchPath === tl('routes.projects') ? 'NEW' : 'RES'
);

watch(projectBuyType, type => {
  if (type === 'NEW') {
    navigateTo(localePath(`/${t('routes.projects')}`));
  } else if (
    type === 'RES' ||
    type === 'RNT' ||
    type === 'SOL' ||
    type === 'RTD'
  ) {
    navigateTo(
      localePath(`/${t('routes.properties')}/${t(`paramsAliases.${type}`)}`)
    );
  }
});

const filteredSubTypesOptions = computed(() => {
  if (!searchQueryParams.value.propertyType?.length) {
    return propertySubTypesOptions;
  }

  return propertySubTypesOptions.filter(
    option =>
      searchQueryParams.value.propertyType.includes(option.typeValue) &&
      (option.value !== 'building' || props.searchPath !== 'new-projects')
  );
});

let lastSavedSearch = '';

async function saveSearch() {
  let check = false;

  if (!isAuthenticated.value) {
    check = true;
    authModalPromise.value = new ExternalPromise<void>();
    await authModalPromise.value;
    authModalPromise.value = null;
    if (!isAuthenticated.value) {
      return;
    }
  }

  const params = getSearchParameters(t, { omit: ['page'] }).join('/');

  if (lastSavedSearch !== params) {
    lastSavedSearch = params;
    searchSaved.value = true;
    const queryParams = getSearchQueryParams();
    triggerEvent('pe23281212_search_save', { saved: params });
    return _saveSearch(searchSummary.value, params, queryParams, check);
  }
}

async function initialize() {
  await locationsPromise.then(() => updateCountyOptions());
  await updateValuesFromPathParameters(false);
  if (searchQueryParams.value.neighborhood) {
    await updateNeighborhoodOptions();
    setNeighborhood({
      value: searchQueryParams.value.neighborhood,
      county: searchQueryParams.value.county,
      city: searchQueryParams.value.city,
      zipcode: searchQueryParams.value.zipcode
    });
  }

  if (!process.client) {
    // only load on server side
    await triggerSearch();
    await updateCardsData();
  }

  return {
    searchQueryParams: searchQueryParams.value,
    searchQueryLocation: searchQueryLocation.value,
    page: page.value,
    bounds: bounds.value,
    lastRequestUrl: lastRequestUrl.value,
    locationJustChanged: locationJustChanged.value,
    countyOptions: countyOptions.value,
    loadedNeighborhoodOptions: loadedNeighborhoodOptions.value,
    baseResult: baseResult.value,
    cardsData: cardsData.value,
    totalEntries: totalEntries.value,
    valuesUpdatedForFirstTime: valuesUpdatedForFirstTime.value
  };
}

onMounted(() => {
  console.log('mounted');
  const onResize = () => {
    isInMobile.value = window.innerWidth < 800;
  };

  window.addEventListener('resize', onResize);
  onResize();

  if (searchAsync.data.value) {
    nextTick(() => {
      updateMap();
    });
  }

  window.addEventListener(
    'popstate',
    () => {
      window.goingBack = true;
    },
    false
  );

  if (!countyOptions.value.length) {
    locationsPromise.then(() => updateCountyOptions());
  }

  if (searchStore.view === 'map') {
    showMap.value = true;
  }

  if (searchStore.customPolygon && window.goingBack && !customPolygon.value) {
    customPolygon.value = searchStore.customPolygon;
  } else {
    resetCustomPolygon();
  }
});

const searchAsync = await useServerAsyncData(
  'search-data',
  initialize,
  console.error
);

let link: string = '';

if (searchAsync.data.value) {
  searchQueryParams.value = searchAsync.data.value.searchQueryParams;
  searchQueryLocation.value = searchAsync.data.value.searchQueryLocation;
  page.value = searchAsync.data.value.page;
  bounds.value = searchAsync.data.value.bounds;
  lastRequestUrl.value = searchAsync.data.value.lastRequestUrl;
  locationJustChanged.value = searchAsync.data.value.locationJustChanged;
  countyOptions.value = searchAsync.data.value.countyOptions;
  loadedNeighborhoodOptions.value =
    searchAsync.data.value.loadedNeighborhoodOptions;
  baseResult.value = searchAsync.data.value.baseResult;
  cardsData.value = searchAsync.data.value.cardsData;
  totalEntries.value = searchAsync.data.value.totalEntries;
  valuesUpdatedForFirstTime.value =
    searchAsync.data.value.valuesUpdatedForFirstTime;

  link = useCanonicalLink((t, linkLocale) => {
    const path = getSearchParameters(t).join('/');

    const searchPath =
      props.searchPath === tl('routes.projects')
        ? t('routes.projects')
        : t('routes.properties');

    return localePath(`/${searchPath}` + (path ? `/${path}` : ''), linkLocale);
  }).link;
}

const breadcrumbs = computed(() => {
  const query: Record<string, QueryValue> = { ...searchQueryParams.value };

  if (searchQueryLocation.value?.value) {
    query.location = searchQueryLocation.value.value;
  }

  const breadcrumbs = getBreadcrumbs(query, props.searchPath);

  const searchPath =
    props.searchPath === tl('routes.projects')
      ? t('routes.projects')
      : t('routes.properties');

  if (!breadcrumbs[breadcrumbs.length - 1].item) {
    breadcrumbs[breadcrumbs.length - 1] = {
      ...breadcrumbs[breadcrumbs.length - 1],
      item: `/${searchPath}/${getSearchParameters(t).join('/')}`,
      interactuable: false
    };
  }

  return breadcrumbs;
});

useSchemaOrg([
  ...useCommonSchemaOrg(link),
  defineBreadcrumb({
    '@id': link + '#breadcrumb',
    itemListElement: breadcrumbs.value.map(({ name, item }) => ({ name, item }))
  })
]);

const { getSearchSeo } = useGetSeo();

useSeoExtended({
  title: () =>
    getSearchSeo(
      {
        ...searchQueryParams.value,
        location: searchQueryValue.value,
        isProject: props.searchPath !== tl('routes.properties'),
        buyType:
          props.searchPath === tl('routes.projects', 'en')
            ? 'NEW'
            : searchingBuildings.value
            ? 'RES'
            : searchQueryParams.value.buyType
      },
      'title'
    ),
  description: () =>
    getSearchSeo(
      {
        ...searchQueryParams.value,
        location: searchQueryValue.value,
        isProject: props.searchPath !== tl('routes.properties'),
        buyType:
          props.searchPath === tl('routes.projects', 'en')
            ? 'NEW'
            : searchingBuildings.value
            ? 'RES'
            : searchQueryParams.value.buyType
      },
      'description'
    ),
  image: () => {
    const firstResultWithData = baseResult.value?.hits?.find(
      hit => cardsData.value[hit.document.id]
    );

    const data = cardsData.value[firstResultWithData?.document?.id];
    return data?.rawImages?.[0] || '/images/pfs-placeholder.svg';
  },
  slice: true
});

const noIndex = computed(() => {
  return (
    page.value > 1 ||
    searchQueryParams.value.buyType === 'SOL' ||
    searchQueryParams.value.buyType === 'RTD'
  );
});

useHead(() =>
  noIndex.value ? { meta: [{ name: 'robots', content: 'noindex' }] } : {}
);
</script>

<style lang="scss">
html:has(#map-container.show-map),
html:has(#filters-button.popover-active) {
  @apply max-md:overflow-hidden;
}

.search-page {
  .search-controls-button {
    @apply rounded text-sm h-[36px] md:h-[42px] pl-2 md:pl-2 pr-0.5 md:pr-1;
    // with aira-expanded="true"
    &[aria-expanded='true'],
    &[data-popper-shown],
    .popover-active &,
    &.popover-active {
      .mdi-icon {
        @apply rotate-180;
      }
    }
  }

  .active-filter-on-selected.option-selected,
  .active-filter {
    @apply relative;
    &::after {
      content: '';
      @apply absolute -top-1 -right-1 w-3 h-3 rounded-full bg-primary;
    }
  }

  #results,
  #results-placeholders {
    @apply flex; // grid grid-flow-row-dense auto-rows-max;
    @apply gap-x-4 w-full h-full;
    @apply max-md:flex-wrap;
  }

  .results-section {
    @apply order-1;
    @apply min-h-[24px];
    @apply w-full;
    @apply flex flex-wrap justify-center items-start content-start md:justify-start gap-4;
  }

  .disable-cards {
    .result-card,
    .category-title {
      @apply pointer-events-none opacity-50;
    }
  }

  .results-section {
    @apply sm:max-w-[100%] md:max-w-[456px] lg:max-w-[692px];
  }

  .side-section {
    @apply order-2 flex-1;
    @apply md:h-[calc(100vh-100px)] top-[60px];
    @apply md:sticky md:top-[147px] md:max-h-[calc(100svh-147px)];
    @apply flex flex-col max-md:items-center;
  }

  #map-container {
    @apply invisible pointer-events-none md:pointer-events-auto md:visible md:opacity-100 #{!important};
    @apply relative opacity-0 transition-all duration-[0.21];
    @apply grow w-full min-w-[20px] min-h-[380px];
    @apply max-md:top-[60px] max-md:h-[100lvh];
    @apply md:max-h-[calc(100svh-147px)];
    @apply bg-white;
    @apply max-md:fixed max-md:left-0 max-md:w-full max-md:z-[55];
    @apply md:w-[calc(100%+16px)];

    &.show-map {
      @apply visible opacity-100 pointer-events-auto #{!important};
    }

    .g-map {
      @apply w-full h-full z-[55];
      @apply max-md:fixed max-md:bottom-[0px] max-md:top-[60px];
      @apply max-md:h-auto #{!important};
      @apply md:border-l border-line md:overflow-hidden;
    }

    &.show-map {
      @apply visible opacity-100 pointer-events-auto #{!important};
    }
  }

  .search-bar {
    input {
      @apply py-1 px-2;
    }
    &,
    & input {
      @apply h-[36px] md:h-[42px];
    }
  }
}
</style>

<style scoped lang="scss">
.variant-search {
  @apply max-w-[140px];
  :deep(.button-inner-container) {
    @apply gap-0 whitespace-nowrap text-ellipsis #{!important};
    span {
      @apply w-full text-ellipsis overflow-hidden #{!important};
    }
  }
  :deep(button) {
    @apply flex-nowrap gap-0 pr-1 #{!important};
    span {
      @apply mr-0 text-center #{!important};
    }
  }
  :deep(.mdi-icon) {
    @apply min-w-6;
  }
  :deep(.selector-buttonTextDefault) {
    @apply flex-1 text-center;
  }
}
</style>
